// paex-fulfil.js — BOPIS fulfilment mode core (Track 3 step 3, lane B, 2026-10-22).
// ONE global, window.paexFulfil, built exactly per the seams contract §3.2:
// the cart mode `paex_fulfil` ∈ ship | pickup:<loc> | local:<loc> (one cart =
// one service, never mixed), the shared stock pre-check every add path runs,
// the gate PaexCartBridge.mutate('cart/add.js') and the PDP form submit go
// through, and the pure helpers (readyLine / localLine / fmtTime) the PDP
// row, strip, drawer header, sticky bar and chip all consume so one wording
// sits on the page.
//
// Load: printed deferred in layout/theme.liquid right after paex-cart-bridge.js
// (after paex-store-core.js: paexStoreNowParts / paexStoreHoursInfo /
// paexDeliveryDistanceKm / paexZoneFor / paexFsaTable are globals here) and
// BEFORE paex-cart-instant.js, so the capture `submit` listener below runs
// first (document order of deferred scripts).
//
// Strings: none in this file. Every shopper string is a paex.fulfil.* locale
// key printed by snippets/paex-fulfil-texts.liquid into the JSON island
// script[data-paex-fulfil-texts] (plus the shared keys it names). No store
// name, location id, hours, fee, cutoff, prep or hold value is literal: the
// DC id is settings.onlineLocId (PDP embed) / the theme setting (island); the
// store comes from the store blob (chip island → PDP embed → drawer data).
//
// Regression rule (number one concern): a visitor with no cart mode and no
// participating store inside its pickup radius (every Montréal shopper) must
// see no difference. Every entry point checks that first and returns before
// touching the DOM, the cart or the event.
//
// Mode resolution order (§3.3): 1. the cart attribute (carrier span rendered
// in snippets/paex-cart-details.liquid, copied here by paex-cart.js
// syncDetails → syncCarrier) wins always — except when the cart's store offers
// neither service any more (1.10: cleared at boot); 2. the page selector (select()); 3. default
// per variant: ship when the DC has it, else pickup:<store> when the store
// participates, is not paused today and has it, else ship. Outside the radius:
// always ship, nothing written. Empty cart after a swap → set(null).
//
// Events (§3.6): paex:fulfil-change (set() after cart/update.js resolves, once
// on boot when a mode exists), paex:fulfil-select (select()),
// paex:fulfil-intercept (gate() conflict / unavailable — cancelable: the drawer
// UI calls preventDefault() to own it; otherwise a native toast backstops),
// paex:fulfil-origin (addition: the async origin / resolved store settled).
(function () {
  'use strict';
  if (window.paexFulfil) return;

  var EVT_CHANGE = 'paex:fulfil-change';
  var EVT_SELECT = 'paex:fulfil-select';
  var EVT_INTERCEPT = 'paex:fulfil-intercept';
  var EVT_ORIGIN = 'paex:fulfil-origin';
  var ASIDE_KEY = 'paex:aside';
  var STOCK_KEY = 'paex:stock:2'; // :2 — drops caches poisoned by the native product JSON (Oct 22)
  var STOCK_TTL = 10 * 60 * 1000; // same horizon as the RCC inventory cache
  var CART_TTL = 60 * 1000;
  var FORM_SELECTOR = 'product-form form, form[data-type="add-to-cart-form"], form[action*="/cart/add"]';

  /* ------------------------------------------------------------------ *
   * Texts island + tiny template helper (no innerHTML anywhere)
   * ------------------------------------------------------------------ */
  function parseIsland(selector) {
    var el = document.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (error) { return null; }
  }
  var island = parseIsland('script[data-paex-fulfil-texts]') || {};
  var TEXTS = island.texts || {};
  var SHARED = island.shared || {};
  var DAYS = island.days || [];
  var SETTINGS = island.settings || {};
  var LANG = String(island.lang || (window.Shopify && Shopify.locale) || 'en').toLowerCase();
  var TWELVE_HOUR = !/^fr/.test(LANG); // plan 6b-3: EN 12-hour, FR « 21 h »

  function fmt(template, values) {
    return String(template == null ? '' : template).replace(/\{(\w+)\}/g, function (match, key) {
      return values && Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
    });
  }
  // Built-in defaults for the few keys that must never render as
  // "Translation missing: …" (a locale file that has not synced yet would
  // otherwise leak that text into the header chip's hours line).
  var FALLBACK_TEXTS = TWELVE_HOUR
    ? { fmt_am: '{h}{m} a.m.', fmt_pm: '{h}{m} p.m.', noon: 'noon', midnight: 'midnight' }
    : { fmt_am: '{h} h{m}', fmt_pm: '{h} h{m}', noon: 'midi', midnight: 'minuit' };
  function hasText(key) {
    var v = TEXTS[key];
    return v != null && v !== '' && !/^Translation missing/.test(String(v));
  }
  function t(key, values) {
    var v = hasText(key) ? TEXTS[key] : (FALLBACK_TEXTS[key] != null ? FALLBACK_TEXTS[key] : '');
    return fmt(v, values || {});
  }
  function capitalize(s) {
    s = String(s || '');
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  // plural(key, n): the `_one` form when n = 1 and it exists
  function plural(key, n, values) {
    values = Object.assign({ n: n }, values || {});
    return n === 1 && TEXTS[key + '_one'] != null ? t(key + '_one', values) : t(key, values);
  }
  // shortName(title): the product name before its first comma or " - "
  // (design 4.5/4.6: "Hermione robe S, Cow Zipster XXL"); variantName(): a
  // real variant title ('' for Default Title, which cart.js gives as null);
  // lineName(line): "{short} · {variant}" — the chooser, the dry run, the
  // set-aside rows and the interception sentences name lines with it; the
  // full title stays on the line object (title / product) for a title attribute.
  function shortName(title) {
    var s = String(title == null ? '' : title).trim();
    var cut = s.search(/,| - /);
    return cut > 0 ? s.slice(0, cut).trim() : s;
  }
  function variantName(v) {
    v = String(v == null ? '' : v).trim();
    return v && v !== 'Default Title' ? v : '';
  }
  function lineName(line) {
    if (!line) return '';
    if (line.short) return line.short;
    var v = variantName(line.variant);
    return shortName(line.product || line.title) + (v ? ' · ' + v : '');
  }
  // distinct(list): one entry per display name — two Discount Kit gift lines
  // (tier 0 / tier 1) of the same bag read as one item in the chooser, the dry
  // run and the consequence box; names() joins the distinct list.
  function distinct(list) {
    var seen = {};
    return (list || []).filter(function (line) { var n = lineName(line); if (seen[n]) return false; seen[n] = true; return true; });
  }
  function names(list) {
    return distinct(list).map(lineName).join(', ');
  }
  // The Cart & Checkout Validation Function answers cart/update.js with
  // "{EN sentence} / {FR sentence}" (one message, both languages): keep the
  // shopper's half. Any other message is returned whole.
  function functionMessage(message) {
    var text = String(message || '').trim();
    var parts = text.split(' / ');
    if (parts.length < 2) return text;
    return (/^fr/.test(LANG) ? parts[parts.length - 1] : parts[0]).trim();
  }
  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || island.root || '/';
  }
  function tz() {
    var pdp = pdpEmbed();
    return (pdp && pdp.settings && pdp.settings.edd && pdp.settings.edd.tz) || SETTINGS.tz || 'America/Toronto';
  }
  function onlineLocId() {
    var pdp = pdpEmbed();
    return String((pdp && pdp.settings && pdp.settings.onlineLocId) || SETTINGS.onlineLocId || '');
  }
  function tierTop() {
    var pdp = pdpEmbed();
    return Number((pdp && pdp.settings && pdp.settings.tierTop) || SETTINGS.tierTop) || 10;
  }
  function num(v) {
    var n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  function money(cents) {
    // the shop's price format ("9.99 $", the one every product price uses) before the bridge's
    // money_with_currency form ("CAD $ 9.99"), which belongs to the cart totals only
    var o = window.paexShippingOffer;
    if (o && typeof o.money === 'function') { try { var text = o.money(cents); if (text) return text; } catch (error) { /* ladder below */ } }
    var b = window.PaexCartBridge;
    if (b && typeof b.money === 'function') return b.money(cents);
    return (cents / 100).toFixed(2);
  }
  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[-.]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function dispatch(name, detail, cancelable) {
    var event = new CustomEvent(name, { detail: detail, cancelable: Boolean(cancelable) });
    return document.dispatchEvent(event);
  }

  /* ------------------------------------------------------------------ *
   * State
   * ------------------------------------------------------------------ */
  var state = {
    mode: null,          // last carrier value (parsed) — the truth
    pending: 0,          // set() writes in flight (carrier sync yields to them)
    selection: null,     // page-local radio choice { service, locationId, mode, sectionId }
    hold: 0,             // > 0 while a switch-then-add is in flight: the empty-cart clear waits
    holdUntil: 0,        // ms epoch: a write-then-add (gate) holds the clear until the add lands (or 15 s)
    origin: null,        // shopper origin { lat, lng, source, postal }
    originReady: null,   // Promise of the first resolution
    cart: null,          // /cart.js cache
    cartAt: 0,
    cartInflight: null,
    stockInflight: {},
    blob: null,          // merged static store blob (chip + PDP)
  };

  /* ------------------------------------------------------------------ *
   * Mode accessors
   * ------------------------------------------------------------------ */
  function parseMode(value) {
    var v = String(value == null ? '' : value).trim();
    if (!v) return null;
    if (v === 'ship') return 'ship';
    return /^(pickup|local):\d+$/.test(v) ? v : null;
  }
  function mode() { return state.mode; }
  function service(m) {
    m = arguments.length ? parseMode(m) : state.mode;
    return m ? m.split(':')[0] : null;
  }
  function locationId(m) {
    m = arguments.length ? parseMode(m) : state.mode;
    if (!m) return null;
    var parts = m.split(':');
    return parts.length > 1 ? parts[1] : null;
  }
  function makeMode(svc, loc) {
    if (svc === 'ship') return 'ship';
    if ((svc === 'pickup' || svc === 'local') && loc) return svc + ':' + String(loc);
    return null;
  }

  // §3.7 CSS hooks: html[data-paex-fulfil] + #Cart-Drawer[data-paex-fulfil]
  function stamp() {
    var svc = service();
    [document.documentElement, document.getElementById('Cart-Drawer')].forEach(function (el) {
      if (!el) return;
      if (svc) el.setAttribute('data-paex-fulfil', svc);
      else el.removeAttribute('data-paex-fulfil');
    });
  }

  function changeDetail(prev, source, cart) {
    return {
      mode: state.mode,
      service: service(),
      locationId: locationId(),
      storeId: storeId(),
      prev: prev,
      source: source,
      cart: cart || null,
    };
  }

  // Carrier: <span hidden data-paex-fulfil="…"> beside the count carrier in
  // snippets/paex-cart-details.liquid (re-rendered on every mutation; /cart/add.js
  // answers with the line, not the cart — seams §2.10). paex-cart.js syncDetails()
  // calls syncCarrier() after each swap.
  function readCarrier() {
    var details = document.getElementById('Cart-Drawer-Details');
    if (!details) return null;
    var span = details.querySelector('span[data-paex-fulfil]');
    if (!span) return null;
    var count = details.querySelector('[data-paex-cart-count]');
    return {
      mode: parseMode(span.getAttribute('data-paex-fulfil')),
      count: count ? Number(count.getAttribute('data-paex-cart-count')) : null,
    };
  }
  // hold / held / unhold: the empty-cart clear (syncCarrier) waits while an add
  // is about to follow a write — gate's write() (held until the add's cartUpdate
  // or 15 s) and the dry run's Continue (held until the retried add resolves).
  var HOLD_MS = 15000;
  function hold() { state.holdUntil = Math.max(state.holdUntil, Date.now() + HOLD_MS); }
  function unhold() { state.holdUntil = 0; }
  function held() { return state.hold > 0 || Date.now() < state.holdUntil; }
  function syncCarrier() {
    var carrier = readCarrier();
    if (!carrier) return null;
    if (carrier.count === 0) {
      // An empty cart drops the mode — except in the middle of a switch that
      // an add follows (the dry run's Continue: aside → set → add): the aside
      // can empty the cart for a moment and the repaint must not clear the
      // mode the add is about to use (seen on the preview Oct 22).
      if (state.mode && !state.pending && !held()) set(null, { source: 'clear' }).catch(function () {});
      return carrier;
    }
    if (state.pending) return carrier; // the in-flight write resolves the truth
    if (carrier.mode !== state.mode) {
      var prev = state.mode;
      state.mode = carrier.mode;
      stamp();
      dispatch(EVT_CHANGE, changeDetail(prev, 'drawer', null));
    }
    return carrier;
  }

  // set(mode, opts) → Promise<cart>: PaexCartBridge.setAttributes → stamp() →
  // paex:fulfil-change. Optimistic: the mode flips before the request so the
  // repaint's carrier (same value) raises no second event; a failed write reverts.
  //
  // Gate fix (2026-10-23): the Validation Function runs on cart/update.js too,
  // so writing pickup/local over a cart with a line the store cannot fulfil
  // answers 422 {status, message:"EN / FR", description}. That answer is never
  // silent: recover422() re-runs checkCart(mode) and dispatches the same
  // cancelable paex:fulfil-intercept the gate uses (kind 'conflict' + cartCheck
  // → the dry run; nothing found → kind 'blocked' with the Function's own
  // sentence and "Keep my cart"). The rejection then carries error.status,
  // error.message (localised half), error.mode and error.intercept (the
  // intercept verdict) so callers lift the veil without closing the dialog.
  // opts.recover === false skips it (a caller that shows the error itself);
  // opts.recoverLines === false skips the dry run (the write that FOLLOWS a
  // dry run: the message is the only honest answer then); opts.add =
  // { payload, opts } lets the dialog retry the add after a switch.
  function set(newMode, opts) {
    opts = opts || {};
    newMode = parseMode(newMode);
    var prev = state.mode;
    if (!newMode && !prev && !opts.force) return Promise.resolve(null);
    var b = window.PaexCartBridge;
    if (!b || typeof b.setAttributes !== 'function') return Promise.reject(new Error('paexFulfil: cart bridge missing'));
    state.mode = newMode;
    state.pending += 1;
    stamp();
    var bridgeOpts = {};
    if ('drawer' in opts) bridgeOpts.drawer = opts.drawer;
    return b.setAttributes({ paex_fulfil: newMode || '' }, bridgeOpts).then(function (cart) {
      state.pending -= 1;
      if (cart && cart.status) { // Shopify error shape {status, message, description}
        state.mode = prev;
        stamp();
        var error = new Error(functionMessage(cart.message) || cart.description || 'paexFulfil: attribute write failed');
        error.status = num(cart.status);
        error.raw = cart;
        error.mode = newMode;
        error.prev = prev;
        if (newMode && opts.recover !== false && error.status === 422) {
          return recover422(newMode, prev, error, opts).then(function (verdict) {
            error.intercept = verdict;
            throw error;
          }, function () { throw error; });
        }
        throw error;
      }
      if (cart && cart.attributes) state.mode = parseMode(cart.attributes.paex_fulfil);
      rememberCart(cart);
      stamp();
      dispatch(EVT_CHANGE, changeDetail(prev, opts.source || 'pdp', cart));
      return cart;
    }, function (error) {
      state.pending -= 1;
      state.mode = prev;
      stamp();
      throw error;
    });
  }

  // recover422(mode, prev, error, opts) → Promise<intercept verdict>: the
  // Function refused the mode → checkCart(mode); lines it names → the dry
  // run (kind 'conflict', cartMode = prev, cartCheck); nothing → the message.
  function recover422(newMode, prev, error, opts) {
    var add = opts.add || {};
    var base = { payload: add.payload || null, opts: add.opts || {}, cartMode: prev, wanted: newMode, checks: [], check: null, status: error.status, message: error.message, source: '422' };
    var linesP = opts.recoverLines === false ? Promise.resolve(null) : checkCart(newMode).catch(function () { return null; });
    return linesP.then(function (res) {
      if (res && res.unavailable && res.unavailable.length) {
        return intercept(Object.assign(base, { kind: 'conflict', cartCheck: res, setOpts: { source: opts.source || 'intercept' } }));
      }
      return intercept(Object.assign(base, { kind: 'blocked' }));
    });
  }

  // select(service, locationId, sectionId): page-local radio choice, no write.
  function select(svc, loc, sectionId) {
    if (!svc) {
      state.selection = null;
    } else {
      var m = makeMode(svc, loc);
      state.selection = m ? { service: svc, locationId: svc === 'ship' ? null : String(loc), mode: m, sectionId: sectionId || null } : null;
    }
    dispatch(EVT_SELECT, { service: state.selection ? state.selection.service : null, locationId: state.selection ? state.selection.locationId : null, sectionId: sectionId || null });
    return state.selection;
  }
  function selection() { return state.selection; }

  /* ------------------------------------------------------------------ *
   * Store blob + resolution
   * ------------------------------------------------------------------ */
  var pdpCache;
  function pdpEmbed() {
    if (pdpCache !== undefined) return pdpCache;
    var host = document.querySelector('paex-delivery-method:not([data-original-section])');
    var el = host ? host.querySelector('script[data-paex-delivery-data]') : null;
    var data = null;
    if (el) { try { data = JSON.parse(el.textContent); } catch (error) { data = null; } }
    pdpCache = data ? {
      settings: data.settings || {},
      variants: data.variants || {},
      stores: data.stores || {},
      texts: data.texts || {},
      handle: host.getAttribute('data-product-handle') || null,
      productId: host.getAttribute('data-product-id') || null,
      host: host,
    } : null;
    return pdpCache;
  }
  function mergeStores(target, source) {
    Object.keys(source || {}).forEach(function (id) {
      if (!source[id] || typeof source[id] !== 'object') return;
      target[id] = Object.assign({}, target[id] || {}, source[id]);
    });
  }
  function prune(map) {
    return typeof paexStorePruneExpired === 'function' ? paexStorePruneExpired(map) : map;
  }
  // stores(): chip island → PDP embed → drawer data (Lane D prints
  // script[data-paex-fulfil-store] for the cart's location; it is inside the
  // re-rendered region, so it is read on every call).
  function stores() {
    if (!state.blob) {
      var out = {};
      var chip = parseIsland('script[data-paex-chip-data]');
      if (chip && chip.stores) mergeStores(out, chip.stores);
      var pdp = pdpEmbed();
      if (pdp) mergeStores(out, pdp.stores);
      state.blob = out;
    }
    var drawer = parseIsland('script[data-paex-fulfil-store]');
    if (!drawer || typeof drawer !== 'object') return prune(state.blob);
    var merged = Object.assign({}, state.blob);
    if (drawer.locationId || drawer.id) {
      var one = {};
      one[String(drawer.locationId || drawer.id)] = drawer;
      mergeStores(merged, one);
    } else if (drawer.name) {
      var key = locationId();
      if (key) { var byMode = {}; byMode[key] = drawer; mergeStores(merged, byMode); }
    } else {
      mergeStores(merged, drawer);
    }
    return prune(merged);
  }
  function store(id) {
    id = id == null ? storeId() : String(id);
    return (id && stores()[id]) || null;
  }
  function storeName(id) {
    var s = store(id);
    return s ? String(s.name || '') : '';
  }

  // Explicit (chosen) store: chip cookie → RCC cookie → account preferred store.
  function explicitStoreId() {
    var id = cookie('paex_preferred_store') || cookie('rcc-place-id');
    if (id) return String(id);
    var host = document.querySelector('paex-delivery-method[data-preferred-store-id], paex-store-chip[data-preferred-store-id]');
    return host ? host.getAttribute('data-preferred-store-id') : null;
  }
  // storeId(): cookie → rcc → account → dest → geo (nearest store to the
  // origin, any distance; the pickup radius is participates()'s test), null
  // when nothing resolves.
  function storeId() {
    var all = stores();
    var explicit = explicitStoreId();
    if (explicit && all[explicit]) return explicit;
    if (state.origin && typeof paexFootprint === 'function') {
      var fit = paexFootprint({ lat: state.origin.lat, lng: state.origin.lng }, all, 1e6);
      return fit && fit.nearestId ? String(fit.nearestId) : null;
    }
    return null;
  }

  function fsaUrl() {
    var el = document.querySelector('[data-fsa-url]');
    return (el && el.getAttribute('data-fsa-url')) || SETTINGS.fsaUrl || '';
  }
  function postalish(value) {
    var raw = String(value || '').toUpperCase().replace(/\s+/g, '');
    return /^[A-Z]\d[A-Z](\d[A-Z]\d)?$/.test(raw) ? raw : null;
  }
  function prettyPostal(raw) {
    raw = String(raw || '').toUpperCase().replace(/\s+/g, '');
    return raw.length === 6 ? raw.slice(0, 3) + ' ' + raw.slice(3) : raw;
  }
  function fsaOrigin(place, source) {
    if (typeof paexPostalKeys !== 'function' || typeof paexFsaTable !== 'function') return Promise.resolve(null);
    var keys = paexPostalKeys(place);
    if (!keys) return Promise.resolve(null);
    return paexFsaTable(fsaUrl()).then(function (table) {
      for (var i = 0; i < keys.length; i += 1) {
        var hit = table && table[keys[i]];
        if (hit && isFinite(hit[0]) && isFinite(hit[1])) {
          return { lat: Number(hit[0]), lng: Number(hit[1]), source: source, postal: postalish(place) };
        }
      }
      return null;
    });
  }
  function geoOrigin() {
    var geo = typeof paexDeliveryReadGeo === 'function' ? paexDeliveryReadGeo() : null;
    if (geo && Number.isFinite(geo.lat) && Number.isFinite(geo.lng)) {
      return { lat: geo.lat, lng: geo.lng, source: 'geo', postal: postalish(geo.postal) };
    }
    return null;
  }
  function customerZip() {
    var el = document.querySelector('[data-customer-zip]');
    return el ? el.getAttribute('data-customer-zip') : null;
  }
  // Origin chain: typed destination (coords, else FSA centroid) → account
  // postal (FSA centroid) → IP geo. Async because of the FSA table.
  function resolveOrigin() {
    var dest = typeof paexDestRead === 'function' ? paexDestRead() : null;
    var chain = Promise.resolve(null);
    if (dest && Number.isFinite(dest.lat) && Number.isFinite(dest.lng)) {
      chain = Promise.resolve({ lat: dest.lat, lng: dest.lng, source: 'dest', postal: postalish(dest.postal) || postalish(dest.place) });
    } else if (dest && dest.place) {
      chain = fsaOrigin(dest.place, 'dest');
    }
    return chain.then(function (origin) {
      if (origin) return origin;
      var zip = customerZip();
      return zip ? fsaOrigin(zip, 'account') : null;
    }).then(function (origin) {
      return origin || geoOrigin();
    }).then(function (origin) {
      state.origin = origin || null;
      dispatch(EVT_ORIGIN, { origin: state.origin, storeId: storeId() });
      return state.origin;
    });
  }
  function origin() { return state.origin; }
  // The postal code the local-delivery gate knows about (dest → account → geo → cart edd_postal).
  function knownPostal() {
    var dest = typeof paexDestRead === 'function' ? paexDestRead() : null;
    var p = dest ? (postalish(dest.postal) || postalish(dest.place)) : null;
    if (!p) p = postalish(customerZip());
    if (!p && state.origin && state.origin.postal) p = state.origin.postal;
    if (!p) {
      var geo = typeof paexDeliveryReadGeo === 'function' ? paexDeliveryReadGeo() : null;
      p = geo ? postalish(geo.postal) : null;
    }
    if (!p && state.cart && state.cart.attributes) p = postalish(state.cart.attributes.edd_postal);
    return p;
  }

  /* ------------------------------------------------------------------ *
   * Participation, pauses, blackout
   * ------------------------------------------------------------------ */
  function nowParts(now) {
    if (typeof paexStoreNowParts === 'function') return paexStoreNowParts(now || new Date(), tz());
    var d = now || new Date();
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    return { weekday: d.getDay(), minutes: d.getHours() * 60 + d.getMinutes(), dateKey: d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) };
  }
  // blackoutList(store, svc): fulfilment_blackout_dates (both services) ∪ the
  // service's own list (pickup_blackout_dates / local_delivery_blackout_dates,
  // CHANGES 23 — the blob prints them as pickupBlackoutDates / localBlackoutDates)
  function blackoutList(s, svc) {
    var out = [];
    var push = function (list) {
      if (!Array.isArray(list)) return;
      list.forEach(function (d) { var k = String(d || '').slice(0, 10); if (k && out.indexOf(k) === -1) out.push(k); });
    };
    if (s) {
      push(s.blackoutDates);
      if (svc === 'pickup') push(s.pickupBlackoutDates);
      else if (svc === 'local') push(s.localBlackoutDates);
    }
    return out;
  }
  function isBlackout(s, dateKey, svc) {
    if (!s || !dateKey) return false;
    return blackoutList(s, svc).indexOf(dateKey) !== -1;
  }
  // 'paused' (toggle) | 'blackout' (today in the service's blackout days, store tz) | null
  function pauseReason(s, svc, now) {
    if (!s) return null;
    if (svc === 'pickup' && s.pickupPaused === true) return 'paused';
    if (svc === 'local' && s.localPaused === true) return 'paused';
    return isBlackout(s, nowParts(now).dateKey, svc) ? 'blackout' : null;
  }
  // pausedToday(store, svc) = the service's toggle OR today ∈ blackoutDates ∪ its own list
  function pausedToday(s, svc, now) {
    return pauseReason(s, svc, now) != null;
  }
  function dateAt(key) { return new Date(key + 'T12:00:00Z'); }
  function monthShort(date) {
    try { return new Intl.DateTimeFormat(LANG, { month: 'short', timeZone: 'UTC' }).format(date); } catch (error) { return ''; }
  }
  // the day number inside a range (FR: « 1er » on the first of the month)
  function dayNum(date) {
    var d = date.getUTCDate();
    return !TWELVE_HOUR && d === 1 ? '1er' : String(d);
  }
  // formatRange(fromKey, toKey): range_one "Oct 30" / « le 30 oct. »; range_two
  // "Oct 30–31" / « les 30 et 31 oct. »; range_month (3+ days, one month)
  // "Oct 28–31" / « du 28 au 31 oct. »; range_span "Oct 30–Nov 2" / « du 30 oct. au 2 nov. »
  function formatRange(fromKey, toKey) {
    var a = dateAt(fromKey);
    var b = dateAt(toKey);
    if (fromKey === toKey) return t('range_one', { from: fmtDateShort(a) });
    var vars = { from: fmtDateShort(a), to: fmtDateShort(b), a: dayNum(a), b: dayNum(b), month: monthShort(a) };
    if (fromKey.slice(0, 7) !== toKey.slice(0, 7)) return t('range_span', vars);
    return Math.round((b - a) / 86400000) === 1 ? t('range_two', vars) : t('range_month', vars);
  }
  // blackoutRange(store, svc, now) → the consecutive run of the service's blackout
  // days that contains today, formatted (see formatRange); '' when today is not
  // a blackout day. The paused copy names it: "Not available Oct 30–31" (CHANGES 18).
  function blackoutRange(s, svc, now) {
    now = now || new Date();
    var today = nowParts(now).dateKey;
    if (!today || !isBlackout(s, today, svc)) return '';
    var keyAt = function (offset) { return nowParts(new Date(now.getTime() + offset * 86400000)).dateKey; };
    var first = 0;
    var last = 0;
    while (first > -60 && isBlackout(s, keyAt(first - 1), svc)) first -= 1;
    while (last < 60 && isBlackout(s, keyAt(last + 1), svc)) last += 1;
    return formatRange(keyAt(first), keyAt(last));
  }
  // pauseText(store, svc, now) → the short paused form every surface shares (the
  // PDP rows, lane P5; the cards, lane E5; the chooser and the cart header here):
  // "Not available right now" / « Non disponible pour le moment » for the
  // toggle, "Not available Oct 30–31" / « Non disponible les 30 et 31 oct. »
  // when today is a blackout day; '' while the service runs.
  function pauseText(s, svc, now) {
    var pr = pauseReason(s, svc, now);
    if (!pr) return '';
    var range = pr === 'blackout' ? blackoutRange(s, svc, now) : '';
    return range ? t('paused_dates', { range: range }) : t('paused_now');
  }
  // pauseFragment(): the same, lowercase, for the {ready} slot of a header line
  // ("Pickup at {store} · not available right now", design 4.4)
  function pauseFragment(s, svc, now) {
    var pr = pauseReason(s, svc, now);
    if (!pr) return '';
    var range = pr === 'blackout' ? blackoutRange(s, svc, now) : '';
    return range ? t('header_paused_dates', { range: range }) : t('header_paused_now');
  }
  // fmtDateShort(date): "Nov 1" / « 1er nov. » — the EDD formatter when it is
  // on the page (one date style per page), else the same Intl shape; FR gets
  // the ordinal on the first of the month (plan 6c copy, finding 52).
  function fmtDateShort(date) {
    var out = '';
    if (typeof paexEddFormatDate === 'function') { try { out = paexEddFormatDate(date, true) || ''; } catch (error) { out = ''; } }
    if (!out) { try { out = new Intl.DateTimeFormat(LANG, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date); } catch (error) { out = date.toISOString().slice(5, 10); } }
    if (!TWELVE_HOUR && date.getUTCDate() === 1) out = out.replace(/(^|\s)1(?=\s|$)/, '$11er');
    return out;
  }
  // pausedBack(store, now) → "back {date}" / « de retour le {date} »: the first
  // open, non-blackout day after today's blackout run (plan 6c: "the return
  // day computed from the list"); '' when today is not a blackout day (a
  // toggle pause has no end date) — shared by the PDP row, the drawer header,
  // the cart page banner and the chooser.
  function pausedBack(s, now, svc) {
    now = now || new Date();
    if (!s || !blackoutList(s, svc).length) return '';
    var today = nowParts(now).dateKey;
    if (!today || !isBlackout(s, today, svc)) return '';
    for (var offset = 1; offset <= 60; offset += 1) {
      var parts = nowParts(new Date(now.getTime() + offset * 86400000));
      if (!parts.dateKey || isBlackout(s, parts.dateKey, svc)) continue;
      if (s.hours && !dayHours(s, offset, now, svc)) continue; // closed that day (holiday / no hours)
      return t('paused_back', { date: fmtDateShort(new Date(parts.dateKey + 'T12:00:00Z')) });
    }
    return '';
  }
  // The shared 75 km footprint (design 0.3 / changelog §1: one distance check
  // for the chip, the PDP module and the selector gate — never a per-store
  // radius): the PDP embed's settings.radiusKm, else the texts island's, else 75.
  // The buy-online radius (theme setting paex_bopis_radius_km, 75 km by default) printed by the texts
  // island. NOT the store-chip footprint (paex_footprint_radius_km, 150 km on live): that one decides
  // who sees the store experience at all; this one decides who is offered pickup / same-day.
  function footprintRadius() {
    return num(SETTINGS.radiusKm) || 75;
  }
  // true while the cart is in pickup / same-day (any location): the store
  // experience applies wherever the shopper is (design 0.3)
  function cartInStoreService() {
    var svc = service();
    return svc === 'pickup' || svc === 'local';
  }
  // participates(storeId) → { pickup, local, pickupPaused, localPaused,
  // pausedToday:{pickup,local}, radiusKm (the shared footprint), inRadius,
  // store, storeId, distanceKm, explicit, gated }. gated = inRadius && (pickup
  // || local), OR the cart is already in pickup / same-day (design 0.3).
  function participates(id) {
    id = id == null ? storeId() : String(id);
    var s = (id && stores()[id]) || null;
    var res = {
      storeId: id || null, store: s, pickup: false, local: false, pickupPaused: false, localPaused: false,
      pausedToday: { pickup: false, local: false }, radiusKm: footprintRadius(), inRadius: false, distanceKm: null, explicit: false, gated: false,
    };
    if (!s) { res.gated = cartInStoreService(); return res; }
    res.pickup = s.pickup === true;
    res.local = s.local === true;
    res.pickupPaused = s.pickupPaused === true;
    res.localPaused = s.localPaused === true;
    res.pausedToday = { pickup: pausedToday(s, 'pickup'), local: pausedToday(s, 'local') };
    var explicit = explicitStoreId();
    if (explicit && String(explicit) === id) {
      res.inRadius = true; // a chosen store counts as inside
      res.explicit = true;
    } else if (state.origin && Number.isFinite(Number(s.lat)) && Number.isFinite(Number(s.lng)) && res.radiusKm > 0 && typeof paexDeliveryDistanceKm === 'function') {
      res.distanceKm = paexDeliveryDistanceKm(state.origin.lat, state.origin.lng, Number(s.lat), Number(s.lng));
      res.inRadius = res.distanceKm <= res.radiusKm;
    }
    res.gated = (res.inRadius && (res.pickup || res.local)) || cartInStoreService();
    return res;
  }
  // 1.10: the cart's store offers neither service any more (pickup_enabled and
  // local_delivery_enabled both off) — the mode is cleared at boot so the drawer
  // and the cart page render as live (the Liquid first paint skips the header too)
  function modeRetired() {
    var loc = locationId();
    if (!loc) return false;
    var s = store(loc);
    return Boolean(s) && s.pickup !== true && s.local !== true;
  }
  // true when the store experience with the selector applies to this visitor
  function gated() {
    return participates(storeId()).gated;
  }

  /* ------------------------------------------------------------------ *
   * Stock: PDP embed first, else ?view=paex-stock (Lane E), sessionStorage
   * ------------------------------------------------------------------ */
  function normalizeEmbed(pdp) {
    var out = {};
    var dc = onlineLocId();
    Object.keys(pdp.variants).forEach(function (vid) {
      var raw = pdp.variants[vid] || {};
      var entry = { id: vid, a: raw._a === 1 || raw._a === true ? 1 : (raw._a === 0 ? 0 : null), dc: null, stores: {}, su: null };
      Object.keys(raw).forEach(function (key) {
        if (key === '_a') return;
        // DC: a missing or null entry means "never synced" → unknown → ship allowed
        // (matches the block's Liquid flag and the Validation Function's rule c).
        // Store: a missing or null entry means 0 → not available for pickup/local
        // (matches the Validation Function's rule b, so no dead end at checkout).
        if (key === dc) entry.dc = raw[key] == null ? null : num(raw[key]);
        else entry.stores[key] = raw[key] == null ? 0 : num(raw[key]);
      });
      out[vid] = entry;
    });
    return out;
  }
  function normalizeView(data) {
    var out = {};
    if (!data) return out;
    // the product's native JSON ({ product: {…} }) or any other shape that is
    // not the stock view → nothing (never cache it as "no stock anywhere")
    if (data.product && !data.variants) return out;
    var list = data.variants || data;
    var push = function (entry, key) {
      if (!entry || typeof entry !== 'object') return;
      // a stock entry carries at least one of a / dc / stores
      if (!('a' in entry) && !('dc' in entry) && !('stores' in entry)) return;
      var id = String(entry.id || key || '');
      if (!id || !/^\d+$/.test(id)) return;
      out[id] = { id: id, a: entry.a == null ? null : (entry.a ? 1 : 0), dc: entry.dc == null ? null : num(entry.dc), stores: entry.stores && typeof entry.stores === 'object' ? entry.stores : {}, su: Array.isArray(entry.su) ? entry.su : null };
    };
    if (Array.isArray(list)) list.forEach(function (e) { push(e, null); });
    else Object.keys(list).forEach(function (k) { push(list[k], k); });
    return out;
  }
  function stockCacheRead() {
    try { return JSON.parse(sessionStorage.getItem(STOCK_KEY) || '{}') || {}; } catch (error) { return {}; }
  }
  function stockCacheWrite(cache) {
    try { sessionStorage.setItem(STOCK_KEY, JSON.stringify(cache)); } catch (error) { /* storage-less: memory only */ }
  }
  var stockMemory = {};
  function stockCached(handle) {
    var hit = stockMemory[handle] || stockCacheRead()[handle];
    return hit && Date.now() - hit.t < STOCK_TTL ? hit.data : null;
  }
  function stockRemember(handle, data) {
    var cache = stockCacheRead();
    cache[handle] = { t: Date.now(), data: data };
    stockMemory[handle] = cache[handle];
    stockCacheWrite(cache);
  }
  function stockFind(variantId) {
    var key = String(variantId);
    var cache = Object.assign({}, stockCacheRead(), stockMemory);
    var handles = Object.keys(cache);
    for (var i = 0; i < handles.length; i += 1) {
      var hit = cache[handles[i]];
      if (hit && hit.data && hit.data[key] && Date.now() - hit.t < STOCK_TTL) return hit.data[key];
    }
    return null;
  }
  function handleOf(ref) {
    if (!ref) return null;
    if (typeof ref === 'object') return ref.handle || null;
    var s = String(ref).trim();
    if (!s || /^\d+$/.test(s)) return null;
    return s.replace(/^.*\/products\//, '').replace(/[?#].*$/, '');
  }
  // stock(productIdOrHandle) → Promise<{ [variantId]: { a, dc, stores:{loc:qty}, su } }>
  function stock(ref) {
    var pdp = pdpEmbed();
    var handle = handleOf(ref);
    var id = ref && typeof ref === 'object' ? ref.id : (typeof ref === 'string' || typeof ref === 'number') && /^\d+$/.test(String(ref)) ? String(ref) : null;
    if (pdp && (ref == null || (handle && handle === pdp.handle) || (id && id === pdp.productId))) {
      return Promise.resolve(normalizeEmbed(pdp));
    }
    if (!handle) return Promise.resolve({});
    var cached = stockCached(handle);
    if (cached) return Promise.resolve(cached);
    if (state.stockInflight[handle]) return state.stockInflight[handle];
    var url = root() + 'products/' + encodeURIComponent(handle) + '?view=' + encodeURIComponent(SETTINGS.stockView || 'paex-stock');
    // No "Accept: application/json" here: Shopify answers a /products/<handle>
    // request that asks for JSON with the product's native JSON ({ product: … })
    // and ignores ?view= — the alternate template only renders for an HTML-ish
    // Accept (seen on the preview, Oct 22: every non-PDP variant came back
    // "unknown" and the dry run named nothing).
    state.stockInflight[handle] = fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html,*/*;q=0.8' } })
      .then(function (response) { return response.ok ? response.text() : null; }) // 404 (view not deployed) → {}
      .then(function (text) {
        if (!text) return {};
        var data = null;
        try { data = JSON.parse(text); } catch (error) {
          var m = text.match(/\{[\s\S]*\}/); // tolerate stray whitespace / layout leftovers
          try { data = m ? JSON.parse(m[0]) : null; } catch (e2) { data = null; }
        }
        var normalized = normalizeView(data);
        if (Object.keys(normalized).length) stockRemember(handle, normalized);
        return normalized;
      })
      .catch(function () { return {}; })
      .then(function (result) { delete state.stockInflight[handle]; return result; });
    return state.stockInflight[handle];
  }
  // One variant's entry: PDP embed → stock(opts.handle) → any cached product
  function variantStock(variantId, opts) {
    var key = String(variantId);
    var pdp = pdpEmbed();
    if (pdp && pdp.variants[key]) return Promise.resolve(normalizeEmbed(pdp)[key] || null);
    if (opts && opts.stock && opts.stock[key]) return Promise.resolve(opts.stock[key]);
    var handle = handleOf(opts && (opts.handle || opts.url));
    if (handle) return stock(handle).then(function (map) { return map[key] || stockFind(key); });
    return Promise.resolve(stockFind(key));
  }

  /* ------------------------------------------------------------------ *
   * Cart cache (/cart.js lazily, invalidated per cartUpdate)
   * ------------------------------------------------------------------ */
  function looksLikeCart(data) {
    return Boolean(data && Array.isArray(data.items) && typeof data.item_count === 'number');
  }
  function rememberCart(data) {
    if (looksLikeCart(data)) { state.cart = data; state.cartAt = Date.now(); }
    else { state.cart = null; state.cartAt = 0; }
  }
  function cart(force) {
    if (!force && state.cart && Date.now() - state.cartAt < CART_TTL) return Promise.resolve(state.cart);
    if (state.cartInflight) return state.cartInflight;
    state.cartInflight = fetch(root() + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (data) { if (looksLikeCart(data)) rememberCart(data); return state.cart; })
      .catch(function () { return state.cart; })
      .then(function (data) { state.cartInflight = null; return data; });
    return state.cartInflight;
  }
  // Discount Kit free-gift line (properties._dk_gift): app-managed quantity,
  // re-added by the app — never listed in the aside with "Add back".
  function isGiftLine(line) {
    var props = line && line.properties;
    return Boolean(props && typeof props === 'object' && props._dk_gift);
  }
  function cartHasLines(cartData) {
    return Boolean(cartData && ((Array.isArray(cartData.items) && cartData.items.length) || num(cartData.item_count) > 0));
  }
  function inCart(cartData, variantId) {
    var key = String(variantId);
    var total = 0;
    ((cartData && cartData.items) || []).forEach(function (line) {
      if (String(line.variant_id) === key) total += num(line.quantity);
    });
    return total;
  }

  /* ------------------------------------------------------------------ *
   * check / checkCart
   * ------------------------------------------------------------------ */
  function evalEntry(entry, svc, loc, qty, already) {
    var res = { ok: true, reason: null, available: null, cap: null, unknown: false };
    if (!entry) { res.unknown = true; return res; }
    var available = svc === 'ship' ? entry.dc : (entry.stores ? entry.stores[String(loc)] : null);
    if (available == null) { res.unknown = true; return res; } // no entry for that location: fail open, the Validation Function is the backstop
    available = Math.max(0, Math.min(num(available), tierTop()));
    res.available = available;
    res.cap = Math.max(0, available - num(already));
    if (available <= 0) { res.ok = false; res.reason = svc === 'ship' ? 'dc_out' : 'store_out'; }
    else if (num(qty) > res.cap) { res.ok = false; res.reason = 'over_qty'; }
    return res;
  }
  // Store-level gate shared by check/checkCart for pickup/local: null when fine.
  function storeGate(svc, loc, now) {
    if (svc === 'ship') return null;
    var p = participates(loc);
    if (!p.store || !p[svc]) return { reason: 'not_participating', participation: p };
    var pr = pauseReason(p.store, svc, now);
    if (pr) return { reason: pr, participation: p };
    if (svc === 'local') {
      var ll = localLine(p.store, knownPostal(), now);
      if (!ll.ok) return { reason: ll.reason, participation: p, local: ll };
      return { reason: null, participation: p, local: ll };
    }
    // pickup minimum (Sasha 2026-10-24: "I don't want orders at 6 $" + "same amount as same-day, same field"):
    // the store's local_delivery_minimum (blob localMin, cents) applies to pickup too; null = no minimum
    var pm = moneyCents(p.store.localMin);
    return { reason: null, participation: p, pickup: { min: pm != null && pm > 0 ? pm : null } };
  }
  // the cart-level minimum of a store service (pickup / same-day) from a storeGate() answer, cents or null
  function gateMinimum(gate) {
    if (!gate) return null;
    if (gate.local && gate.local.min != null) return gate.local.min;
    if (gate.pickup && gate.pickup.min != null) return gate.pickup.min;
    return null;
  }
  function underMinText(svc, amount, min) {
    return t(svc === 'pickup' ? 'pickup_under_min' : 'local_under_min', { amount: money(amount), min: money(min) });
  }
  // check(variantId, mode, qty, opts?) → Promise<{ ok, reason, available, cap }>
  // reason ∈ dc_out | store_out | over_qty | paused | blackout | not_participating
  //        | no_postal | outside_zone | under_min | null
  // cap = available − already in cart for this variant. opts.handle lets a
  // non-PDP caller name the product for the stock view.
  function check(variantId, m, qty, opts) {
    opts = opts || {};
    qty = Math.max(1, num(qty) || 1);
    var modeP = parseMode(m) ? Promise.resolve(parseMode(m)) : defaultMode(variantId, opts);
    return modeP.then(function (resolved) {
      var svc = service(resolved);
      var loc = locationId(resolved);
      var result = { ok: true, reason: null, available: null, cap: null, mode: resolved, service: svc, locationId: loc, variantId: String(variantId), qty: qty };
      var gate = storeGate(svc, loc);
      if (gate) {
        if (gate.local) result.local = gate.local;
        if (gate.reason) { result.ok = false; result.reason = gate.reason; return result; }
      }
      return Promise.all([variantStock(variantId, opts), cart()]).then(function (pair) {
        var ev = evalEntry(pair[0], svc, loc, qty, inCart(pair[1], variantId));
        result.ok = ev.ok;
        result.reason = ev.reason;
        result.available = ev.available;
        result.cap = ev.cap;
        result.unknown = ev.unknown;
        result.inCart = inCart(pair[1], variantId);
        return result;
      });
    });
  }
  // checkCart(mode) → Promise<{ ok, total, unavailable:[{ key, title, variantId, qty, reason }] }>
  // (+ cartReason for a cart-level block: not_participating | paused | blackout |
  // no_postal | outside_zone | under_min, with local / underMin details)
  function checkCart(m) {
    m = parseMode(m);
    if (!m) return Promise.resolve({ ok: true, total: 0, unavailable: [], mode: null });
    var svc = service(m);
    var loc = locationId(m);
    return cart(true).then(function (cartData) {
      var items = (cartData && cartData.items) || [];
      return Promise.all(items.map(function (line) {
        return variantStock(line.variant_id, { handle: line.handle }).then(function (entry) { return { line: line, entry: entry }; });
      })).then(function (rows) {
        var unavailable = [];
        rows.forEach(function (row) {
          var line = row.line;
          var gift = isGiftLine(line);
          var ev = evalEntry(row.entry, svc, loc, line.quantity, 0);
          // Discount Kit gift line (plan 6b decision 4: treated like any line):
          // the Validation Function counts it against the store entry, so an
          // UNKNOWN entry is 0 for pickup/local here too — the dry run names
          // the gift instead of the Function's 422 naming nothing.
          if (gift && svc !== 'ship' && ev.unknown) ev = { ok: false, reason: 'store_out', available: 0, cap: 0, unknown: false };
          if (ev.ok) return;
          unavailable.push({
            key: line.key, title: line.product_title + (line.variant_title ? ' · ' + line.variant_title : ''),
            product: line.product_title, variant: variantName(line.variant_title),
            short: lineName({ product: line.product_title, variant: line.variant_title }),
            variantId: line.variant_id, qty: line.quantity, reason: ev.reason, available: ev.available,
            handle: line.handle, url: line.url, image: line.image, productId: line.product_id, gift: gift,
          });
        });
        var res = { ok: unavailable.length === 0, total: items.length, unavailable: unavailable, mode: m, service: svc, locationId: loc, cartReason: null };
        var gate = storeGate(svc, loc);
        if (gate) {
          if (gate.local) res.local = gate.local;
          if (gate.reason) { res.ok = false; res.cartReason = gate.reason; }
          else {
            // the minimum order (Sasha 2026-10-24: "I need to be able to add the items still to get my cart to
            // the minimum") NEVER blocks an add or a switch — it is information here (res.underMin); only the
            // checkout button holds (fillHeader / syncCta)
            var gmin = gateMinimum(gate);
            if (gmin != null && cartData && num(cartData.total_price) < gmin) {
              res.underMin = { amount: gmin - num(cartData.total_price), min: gmin, text: underMinText(svc, gmin - num(cartData.total_price), gmin) };
            }
          }
        }
        return res;
      });
    });
  }
  // §3.3 step 3 — default mode for a variant: ship when the DC has it, else
  // pickup:<store> when the resolved store participates, is open to pickup
  // today and has it, else ship.
  function defaultMode(variantId, opts) {
    var p = participates(storeId());
    return variantStock(variantId, opts).then(function (entry) {
      if (!entry) return 'ship';
      if (num(entry.dc) > 0) return 'ship';
      if (p.gated && p.pickup && !p.pausedToday.pickup && entry.stores && num(entry.stores[String(p.storeId)]) > 0) return 'pickup:' + p.storeId;
      return 'ship';
    });
  }

  /* ------------------------------------------------------------------ *
   * gate(payload, opts) — the bridge / form hook
   * ------------------------------------------------------------------ */
  function normalizeItems(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.items)) {
      return payload.items.filter(function (it) { return it && it.id; }).map(function (it) { return { id: String(it.id), quantity: Math.max(1, num(it.quantity) || 1) }; });
    }
    if (payload.id) return [{ id: String(payload.id), quantity: Math.max(1, num(payload.quantity) || 1) }];
    return [];
  }
  function reasonText(c) {
    if (!c) return '';
    var name = storeName(c.locationId || storeId());
    if (c.reason === 'dc_out') return t('reason_dc_out');
    if (c.reason === 'store_out') return t('reason_store_out', { store: name });
    if (c.reason === 'paused' || c.reason === 'blackout') return t('reason_paused');
    // the cart already holds the store's whole stock (cap 0): "The store’s only one is already in your cart" (never "Only 0 available")
    if (c.reason === 'over_qty') return num(c.cap) > 0 ? t('qty_cap_' + c.service, { n: c.cap }) : plural('qty_cap_none', num(c.available), { n: c.available });
    if (c.reason === 'no_postal') return t('local_postal_missing');
    if (c.reason === 'outside_zone' && c.local) return c.local.text;
    return c.reason || '';
  }
  // dryrunText(target, res): "Switching to {service} will set aside K items…: {names}. Continue?"
  function dryrunText(target, res) {
    var svc = service(target);
    var n = res && res.unavailable ? distinct(res.unavailable).length : 0;
    return plural('dryrun_' + svc, n, { names: names(res && res.unavailable), store: storeName(locationId(target)) });
  }
  // interceptCopy(d) → { title, text, icon } — the interception's words in
  // customer terms (design 3.1 / 3.2 / 5b.5 / 4.6, export4-script.js):
  //  - a cart with lines and no service yet, or a 422 (kind 'conflict' +
  //    cartCheck): the dry run — "Pick up your whole order in store?" +
  //    "Removed: {names}." (warning icon)
  //  - the item cannot follow the cart's service: "This item can’t be picked
  //    up in store" + why ("Not in stock at {store}." / "Not available
  //    online.") + "An order is either shipped or picked up." (warning icon)
  //  - the item fits the cart's service (3.2): "Your order will be picked up
  //    in store" + the split sentence (question icon)
  // Title ≤ 6 words, body ≤ 15 words (changelog §6).
  function interceptCopy(d) {
    var cartSvc = service(d.cartMode);
    var c = d.check || {};
    var wantedSvc = service(d.wanted) || 'ship';
    if (d.kind === 'blocked') return { title: t('switch_q_' + wantedSvc), text: d.message || '', icon: 'warn' };
    if (d.kind === 'conflict' && d.cartCheck && d.cartCheck.unavailable && d.cartCheck.unavailable.length) {
      return { title: t('switch_q_' + wantedSvc), text: dryrunText(d.wanted, d.cartCheck), icon: 'warn' };
    }
    var svc = cartSvc || c.service || wantedSvc;
    // 3.2 (Sasha 2026-10-23): a choice, not a statement — "You chose same-day delivery earlier · Which one do you
    // want to continue with? One order = one service." → Keep {service} / Change to {service}
    if (c.ok !== false) return { title: t('intercept_title_fits_' + svc), text: t('intercept_fits_text'), icon: 'ask' };
    var loc = c.locationId || locationId(d.cartMode) || locationId(d.wanted);
    var why = '';
    if (c.reason === 'dc_out') why = t('intercept_why_online');
    else if (c.reason === 'store_out') why = t('intercept_why_store', { store: storeName(loc) });
    else if (c.reason === 'paused' || c.reason === 'blackout') why = pauseText(store(loc), c.service || svc);
    else why = capitalize(reasonText(c));
    if (why && !/[.!?]$/.test(why)) why += '.';
    return { title: t('intercept_title_missing_' + svc), text: [why, t('intercept_split')].filter(Boolean).join(' '), icon: 'warn' };
  }
  // sentence(text): a template filled with a fragment that already ends with an
  // abbreviation dot (FR « le 30 oct. ») never prints ".." before the next sentence
  function sentence(text) {
    return String(text || '').replace(/\.\.(\s|$)/g, '.$1');
  }
  // The sentence the toast backstop opens with (title. body); a 422 keeps the
  // Function's own message only.
  function interceptText(d) {
    if (d.kind === 'blocked') return d.message || '';
    var copy = interceptCopy(d);
    if (!copy.text) return copy.title;
    return copy.title + (/[.!?]$/.test(copy.title) ? ' ' : '. ') + copy.text;
  }
  function submitForm(form, submitter) {
    form.dataset.paexFulfilOk = '1';
    var sub = submitter && submitter.form === form ? submitter : null;
    if (typeof form.requestSubmit === 'function') {
      try { if (sub) form.requestSubmit(sub); else form.requestSubmit(); return; } catch (error) { /* fall through */ }
    }
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }
  // Re-run the intercepted add in a mode (default: the cart's).
  function retryAdd(d, overrideMode) {
    var opts = Object.assign({}, d.opts || {}, { mode: parseMode(overrideMode) || d.cartMode || d.wanted });
    if (opts.form) {
      var form = opts.form;
      return gate(d.payload, opts).then(function (res) {
        if (res && res.ok) submitForm(form, opts.submitter);
        else if (window.PaexCartInstant) PaexCartInstant.lift(!(res && res.claimed));
        return res;
      });
    }
    var b = window.PaexCartBridge;
    return b && typeof b.mutate === 'function' ? b.mutate('cart/add.js', d.payload, opts) : Promise.resolve(null);
  }
  function intercept(d) {
    d.text = interceptText(d);
    d.reasonText = reasonText(d.check);
    d.store = store(locationId(d.cartMode) || locationId(d.wanted) || storeId());
    d.retry = function (overrideMode) { return d.payload ? retryAdd(d, overrideMode) : Promise.resolve(null); };
    // the dry run's Continue: lines → aside, THEN the mode, THEN the add again
    if (d.cartCheck) {
      d.proceed = function () {
        // the empty-cart clear stays off until the retried add has landed: the
        // counter covers the bridge path (retry resolves with the add), hold()
        // covers the form path (retry resolves at submit, the add's cartUpdate
        // or 15 s releases it)
        state.hold += 1;
        hold();
        var release = function () { state.hold = Math.max(0, state.hold - 1); };
        return switchCart(d.wanted, d.cartCheck, Object.assign({ source: 'intercept' }, d.setOpts || {}))
          .then(function () { return d.retry(d.wanted); })
          .then(function (r) { release(); return r; }, function (e) { release(); throw e; });
      };
    }
    var claimed = !dispatch(EVT_INTERCEPT, d, true); // the drawer UI claims it with preventDefault()
    if (!claimed) { // nobody owns it — never a silent dead end
      try {
        if (typeof webvista !== 'undefined' && webvista && typeof webvista.popToast === 'function' && d.text) webvista.popToast(d.text, 'error');
      } catch (error) { /* toast is best-effort */ }
    }
    return { ok: false, intercepted: true, claimed: claimed, kind: d.kind, cartMode: d.cartMode, wanted: d.wanted, check: d.check, checks: d.checks, cartCheck: d.cartCheck || null, text: d.text, status: d.status || null };
  }
  // gate(payload, opts) → Promise<{ ok:true, mode } | { ok:false }>
  // Resolves the mode (cart attribute → opts.mode / page selection → default),
  // runs check per id / items, dispatches paex:fulfil-intercept on a conflict
  // or an unavailable item, writes the attribute on the first add.
  function gate(payload, opts) {
    opts = opts || {};
    var items = normalizeItems(payload);
    if (!items.length) return Promise.resolve({ ok: true, mode: state.mode, written: false });
    var cartMode = state.mode;
    var explicit = parseMode(opts.mode) || (state.selection && state.selection.mode) || null;
    var p = participates(storeId());
    // Today's behaviour, untouched: no cart mode, no selector, nothing asked.
    if (!cartMode && !explicit && !p.gated) return Promise.resolve({ ok: true, mode: 'ship', written: false });
    var wantedP = explicit || cartMode ? Promise.resolve(explicit || cartMode) : defaultMode(items[0].id, opts);
    return wantedP.then(function (wanted) {
      var runChecks = function (target) {
        return Promise.all(items.map(function (it) { return check(it.id, target, it.quantity, opts); }));
      };
      if (cartMode && wanted !== cartMode) {
        return runChecks(cartMode).then(function (checks) {
          var bad = checks.filter(function (c) { return !c.ok; })[0] || null;
          return intercept({ payload: payload, opts: opts, cartMode: cartMode, wanted: wanted, checks: checks, check: bad || checks[0], kind: 'conflict' });
        });
      }
      var target = cartMode || wanted;
      return runChecks(target).then(function (checks) {
        var bad = checks.filter(function (c) { return !c.ok; })[0] || null;
        if (bad) return intercept({ payload: payload, opts: opts, cartMode: cartMode, wanted: target, checks: checks, check: bad, kind: cartMode ? 'missing' : 'unavailable' });
        if (cartMode) return { ok: true, mode: target, written: false, checks: checks };
        var setOpts = { source: opts.fulfilSource || 'pdp', add: { payload: payload, opts: opts } };
        if ('drawer' in opts) setOpts.drawer = opts.drawer;
        var write = function () {
          hold(); // the add follows this write: an empty-cart repaint in between must not clear the mode
          return set(target, setOpts).then(function () {
            return { ok: true, mode: target, written: true, checks: checks };
          }, function (error) {
            // the Function refused the mode (422): set() already opened the dry
            // run / the message — blocked, the veil lifts, the dialog stays
            if (error && error.intercept) return Object.assign({ error: 'write_failed', mode: target }, error.intercept);
            // any other failure: a ship add needs no attribute; a store service does
            return target === 'ship' ? { ok: true, mode: target, written: false, checks: checks } : { ok: false, error: 'write_failed', mode: target };
          });
        };
        // Gate fix (2026-10-23): a cart that already has lines but no mode is
        // never stamped blindly — the Validation Function would answer 422 on
        // cart/update.js for any line the store cannot fulfil. Same path as a
        // mode change: checkCart(wanted) first; every line fits → write + add;
        // K lines do not → the dry run (kind 'conflict', cartMode null = "no
        // service yet"), Continue = aside → set → add, Cancel = nothing.
        return cart().then(function (cartData) {
          if (!cartHasLines(cartData)) return write();
          return checkCart(target).then(function (res) {
            if (!res.unavailable.length) return write();
            return intercept({ payload: payload, opts: opts, cartMode: null, wanted: target, checks: checks, check: null, cartCheck: res, kind: 'conflict', setOpts: setOpts });
          }, write);
        }, write);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * switchCart — the service switch itself (dry run confirmed): the lines
   * that do not fit go to the aside (cart/change.js quantity 0 each, in the
   * bridge queue), THEN the mode is written. Order matters since the
   * Validation Function runs on cart/update.js: the write only passes once
   * the cart holds nothing the store cannot fulfil.
   * ------------------------------------------------------------------ */
  function similarFor(key, line) {
    var d = document.getElementById('Cart-Drawer');
    var row = d && d.querySelector('[data-paex-line-key="' + String(key).replace(/"/g, '\\"') + '"]');
    return (row && row.getAttribute('data-paex-similar')) || line.url || '';
  }
  // removeLines(lines, prevMode, target): one change.js per line (bubble-only
  // paints until the last one), each line remembered in sessionStorage with
  // the service it was removed for — except a Discount Kit gift line: the app
  // owns it (re-adds it when the cart qualifies), so it is removed for the
  // write but never offered "Add back".
  function removeLines(lines, prevMode, target) {
    var b = window.PaexCartBridge;
    if (!b || typeof b.mutate !== 'function' || !lines || !lines.length) return Promise.resolve();
    var chain = Promise.resolve();
    lines.forEach(function (line, i) {
      chain = chain.then(function () {
        if (!line.gift) {
          aside.add({
            key: line.key, variantId: String(line.variantId), qty: num(line.qty) || 1, title: line.title,
            product: line.product || line.title, variant: line.variant || '', short: line.short || lineName(line),
            handle: line.handle || null, url: line.url || '', image: line.image || '', productId: line.productId || null,
            mode: prevMode, asideFor: service(target), storeName: storeName(locationId(target) || locationId(prevMode)),
            similar: similarFor(line.key, line), reason: line.reason || null,
          });
        }
        var opts = { source: 'paex-fulfil' };
        if (i < lines.length - 1) opts.drawer = null;
        return b.mutate('cart/change.js', { id: line.key, quantity: 0 }, opts).then(function (resp) {
          if (!resp || resp.status) aside.remove(line.key); // the line stayed in the cart: never list it twice
          return resp;
        }, function () { aside.remove(line.key); });
      });
    });
    return chain;
  }
  // switchCart(target, res, setOpts) → Promise<cart>: res = checkCart(target)
  // already run (the dry run the shopper confirmed)
  function switchCart(target, res, setOpts) {
    var prev = state.mode;
    return removeLines((res && res.unavailable) || [], prev, target).then(function () {
      return set(target, Object.assign({ source: 'chooser', recoverLines: false }, setOpts || {}));
    });
  }

  /* ------------------------------------------------------------------ *
   * Time formatting + ready / local lines (pure: store blob + now)
   * ------------------------------------------------------------------ */
  function parseMinutes(value) {
    if (typeof paexStoreParseMinutes === 'function') {
      var v = paexStoreParseMinutes(value);
      if (v != null) return v;
    }
    var m = String(value == null ? '' : value).match(/^\s*(\d{1,2})(?:[:h\s]\s*(\d{2}))?\s*$/i);
    if (!m) return null;
    return parseInt(m[1], 10) * 60 + (m[2] ? parseInt(m[2], 10) : 0);
  }
  // fmtTime(h, m): EN "9 p.m." / "2:30 p.m." / "noon" ; FR « 21 h » / « 14 h 30 » / « midi »
  function fmtTime(h, m) {
    h = num(h); m = num(m);
    h = ((h % 24) + 24) % 24;
    if (m === 0 && h === 12) return t('noon');
    if (m === 0 && h === 0) return t('midnight');
    var mm = m ? (TWELVE_HOUR ? ':' : ' ') + (m < 10 ? '0' : '') + m : '';
    var hh = TWELVE_HOUR ? (h % 12 || 12) : h;
    return t(h < 12 ? 'fmt_am' : 'fmt_pm', { h: hh, m: mm });
  }
  function fmtMinutes(minutes) {
    return fmtTime(Math.floor(minutes / 60), minutes % 60);
  }
  // dayLabel(offset, weekday, sentenceStart): "today" / "tomorrow" / the weekday;
  // capitalised at a sentence start, else as it reads inside a sentence (EN keeps
  // "Sunday", FR lowercases « dimanche » — the locale file capitalises its days)
  function dayLabel(offset, weekday, sentenceStart) {
    var label;
    if (offset === 0) label = SHARED.today || '';
    else if (offset === 1) label = SHARED.tomorrow || '';
    else label = DAYS[weekday] || '';
    if (sentenceStart) return capitalize(label);
    return /^fr/.test(LANG) ? label.charAt(0).toLowerCase() + label.slice(1) : label;
  }
  // Hours of a given day (offset from now, store tz) with holidays + the
  // service's blackout days. → { key, weekday, open, close } or null when closed.
  function dayHours(s, offset, now, svc) {
    var base = now || new Date();
    var parts = nowParts(new Date(base.getTime() + offset * 86400000));
    if (!parts.dateKey) return null;
    if (isBlackout(s, parts.dateKey, svc)) return null;
    var hol = typeof paexStoreHolidayFor === 'function' ? paexStoreHolidayFor(s.holidays, parts.dateKey) : null;
    if (hol && hol.status === 'closed') return null;
    var slot = s.hours && s.hours[String(parts.weekday)];
    var open = slot ? parseMinutes(slot.open) : null;
    var close = slot ? parseMinutes(slot.close) : null;
    var special = typeof paexStoreSpecialRange === 'function' ? paexStoreSpecialRange(hol) : null;
    if (special) { open = special.open; close = special.close; }
    if (open == null || close == null || close <= open) return null;
    return { key: parts.dateKey, weekday: parts.weekday, open: open, close: close, offset: offset };
  }
  function prepText(prepH) {
    return prepH === 1 ? t('prep_hours_one') : t('prep_hours', { n: prepH });
  }
  // the abbreviated prep ("2 h" both languages) for the one-line slots
  function prepShort(prepH) {
    return prepH === 1 ? t('prep_short_one') : t('prep_short', { n: prepH });
  }
  function holdText(days) {
    return days === 1 ? t('hold_days_one') : t('hold_days', { n: days });
  }
  // readyLine(store, now) → THE ready promise, one source for the PDP row, the
  // strip, the drawer header, the cart page banner, the sticky bar, the quick
  // add and the shipping page (D3, 2026-10-23; C5 copy per the design export).
  // Rule (plan 6b / checklist 4): now + prep before closing → ready today; else
  // next opening + prep, never "ready at opening". No hold period on the site
  // (changelog §12: emails only), no parenthetical (CHANGES 19). Fields:
  //   text       the full sentence, capitalised: open "Usually ready in 2 hours"
  //              (ready_text_open); closed "Ready tomorrow around noon" (ready_text_closed)
  //   sub        = text
  //   inline     lowercase verb-first short form for headers / strips / sticky:
  //              open "ready in 2 h" (ready_inline_open, abbreviated prep), closed
  //              "ready tomorrow around noon" (ready_inline_closed)
  //   short / inlineLong  = inline
  //   when       the capitalised time-only form (ready_today / ready_when), '' when
  //              the hours give no answer — never returned as text
  //   base       ready_base (= the open sentence)
  //   closed     false only when the order is ready today during store hours
  //   day        capitalised day label (closed branch) · open  the opening time
  //   dayKey / time / minutes / prep / hold / prepShort / prepHours / holdDays / unknown
  //   (hold / holdDays stay on the object for the emails and the shipping page;
  //   nothing on the site prints them)
  // prep = store.prepHours; holidays via the blob's holiday entries, the pickup
  // blackout days skipped, store time via settings.paex_dispatch_tz.
  function readyLine(s, now) {
    now = now || new Date();
    s = s || {};
    var prepH = num(s.prepHours);
    var holdD = num(s.holdDays);
    var prepMin = Math.round(prepH * 60);
    var pt = prepText(prepH);
    var ps = prepShort(prepH);
    var base = t('ready_base', { prep: pt });
    var open = t('ready_text_open', { prep: pt });
    var inline = t('ready_inline_open', { prep: ps });
    var res = { text: open, sub: open, inline: inline, inlineLong: inline, short: inline, base: base, when: '', dayKey: null, time: null, minutes: null, closed: true, day: null, open: null, prep: pt, hold: holdText(holdD), prepShort: ps, prepHours: prepH, holdDays: holdD };
    if (!s.hours) { res.unknown = true; return res; }
    var nowMin = nowParts(now).minutes;
    for (var offset = 0; offset <= 14; offset += 1) {
      var day = dayHours(s, offset, now, 'pickup');
      if (!day) continue;
      var ready;
      if (offset === 0) {
        if (nowMin >= day.open && nowMin < day.close) {
          ready = nowMin + prepMin;
          if (ready > day.close) continue; // too late today
          res.closed = false;
          res.dayKey = day.key;
          res.minutes = ready;
          res.time = fmtMinutes(ready);
          res.when = t('ready_today', { time: res.time });
          res.day = dayLabel(0, day.weekday, true);
          return res;
        }
        if (nowMin >= day.close) continue;
        // before opening today
      }
      ready = day.open + prepMin;
      if (ready > day.close) continue;
      res.dayKey = day.key;
      res.minutes = ready;
      res.time = fmtMinutes(ready);
      res.day = dayLabel(offset, day.weekday, true);
      res.open = fmtMinutes(day.open);
      res.when = t('ready_when', { day: res.day, time: res.time });
      // the lowercase day inside a sentence ("tomorrow", « demain »; a weekday keeps its locale case)
      var vars = { day: dayLabel(offset, day.weekday, false), time: res.time };
      res.text = t('ready_text_closed', vars);
      res.sub = res.text;
      res.inline = t('ready_inline_closed', vars);
      res.short = res.inline;
      res.inlineLong = res.inline;
      return res;
    }
    res.unknown = true;
    return res;
  }
  // Money fields from the metaobject → cents; null when absent. The store blobs are printed by
  // Liquid, where a money metafield's `.value | json` is the Money drop serialised as an INTEGER
  // of cents (local_delivery_fee 9.99 $ → 999): a number is already cents. A string ("9.99") or
  // a {amount, currency_code} object (the Admin API shape) is in dollars.
  function moneyCents(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? Math.round(value) : null;
    if (typeof value === 'object') value = value.amount != null ? value.amount : value.value;
    if (value == null || value === '') return null;
    var n = parseFloat(String(value).replace(/[^\d.,-]/g, '').replace(',', '.'));
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  }
  // local_delivery_postal_prefixes: list, or "G1,G2 G3;G0A|…" → paexZoneFor rules
  function prefixRules(value) {
    var list = Array.isArray(value) ? value : String(value == null ? '' : value).split(/[\s,;|]+/);
    var rules = [];
    list.forEach(function (item) {
      var prefix = String(item == null ? '' : item).replace(/\s+/g, '').toUpperCase();
      if (!prefix) return;
      rules.push([prefix, 'metro']);
    });
    return rules;
  }
  // Same-day zone without a prefix list (Sasha 2026-10-23: the store's own postal
  // read "outside"): the store's local_delivery_radius_km (blob localRadiusKm)
  // measured from the postal's FSA centroid. The FSA table is the shared
  // paexFsaTable() promise; until it resolves the verdict is unknown (null) and
  // the first resolution re-syncs every consumer through paex:fulfil-change.
  var FSA = null;
  var fsaLoading = false;
  function fsaTableSync() {
    if (FSA) return FSA;
    if (!fsaLoading && typeof paexFsaTable === 'function') {
      fsaLoading = true;
      paexFsaTable(fsaUrl()).then(function (table) {
        FSA = table || {};
        try { document.dispatchEvent(new CustomEvent('paex:fulfil-change', { detail: { source: 'fsa' } })); } catch (e) { /* additive */ }
      }, function () { FSA = {}; });
    }
    return null;
  }
  // km from the postal's FSA centroid to the store, null while the table loads or when unknown
  function postalDistanceKm(postal, s) {
    if (!s || !Number.isFinite(Number(s.lat)) || !Number.isFinite(Number(s.lng))) return null;
    if (typeof paexPostalKeys !== 'function' || typeof paexDeliveryDistanceKm !== 'function') return null;
    var keys = paexPostalKeys(postal);
    if (!keys) return null;
    var table = fsaTableSync();
    if (!table) return null;
    for (var i = 0; i < keys.length; i += 1) {
      var hit = table[keys[i]];
      if (hit && isFinite(hit[0]) && isFinite(hit[1])) return paexDeliveryDistanceKm(Number(hit[0]), Number(hit[1]), Number(s.lat), Number(s.lng));
    }
    return null;
  }
  // inZone(postal, store) → true | false | null (unknown yet): the prefix list when
  // the store has one, else the same-day radius from the FSA centroid
  function inZone(postal, s) {
    var rules = prefixRules(s && s.localPrefixes);
    if (rules.length) return Boolean(zoneFor(postal, rules));
    var r = num(s && s.localRadiusKm);
    if (!(r > 0)) return false;
    var km = postalDistanceKm(postal, s);
    return km == null ? null : km <= r;
  }
  function zoneFor(postal, rules) {
    if (typeof paexZoneFor === 'function') return paexZoneFor(postal, rules);
    var raw = String(postal || '').replace(/\s+/g, '').toUpperCase();
    var best = null, fallback = null;
    rules.forEach(function (rule) {
      if (rule[0] === '*') { fallback = rule[1]; return; }
      if (raw.indexOf(rule[0]) === 0 && (!best || rule[0].length > best[0].length)) best = rule;
    });
    return best ? best[1] : fallback;
  }
  // localLine(store, postal, now) → { ok, reason, text, when, cutoff,
  // cutoffMinutes, time, minutes, day, dayKey, fee, min, feeText, postal }.
  // The cutoff is COMPUTED from the real hours (holiday hours and the same-day
  // blackout days included, checklist 4b / CHANGES 13): today's close − prep
  // (store.prepHours) − the courier buffer (store.courierBufferHours, metaobject
  // courier_buffer_hours, default 1 h); a typed local_delivery_cutoff only caps
  // it earlier. On an open day before the cutoff → ok, text local_today "Order
  // before {cutoff} for today", when = local_when_today "today"; after it, or
  // closed → the next open day whose opening + prep still clears the buffer:
  // text local_next "Delivered {day} around {time}" (time = opening + prep),
  // when = "{day}" lowercase. `cutoff` is the formatted time ("6 p.m." / « 18 h »)
  // the sticky bar (lane P5) prints. reason ∈ not_participating | paused |
  // blackout | no_postal | outside_zone | null. feeText (fee · minimum) stays
  // computed for checkout-side consumers; nothing on the PDP, the quick add or
  // the cart header prints it any more (CHANGES 14).
  function localLine(s, postal, now) {
    now = now || new Date();
    var res = { ok: false, reason: null, text: '', when: '', feeText: '', fee: null, min: null, cutoff: null, cutoffMinutes: null, time: null, minutes: null, day: null, dayKey: null, postal: null };
    if (!s || s.local !== true) { res.reason = 'not_participating'; return res; }
    res.fee = moneyCents(s.localFee);
    res.min = moneyCents(s.localMin);
    if (res.fee != null && res.min != null) res.feeText = t('local_fee_min', { fee: money(res.fee), min: money(res.min) });
    else if (res.fee != null) res.feeText = money(res.fee);
    else res.feeText = t('local_fee_checkout');
    var prepMin = Math.round(num(s.prepHours) * 60);
    var bufferMin = Math.round((s.courierBufferHours == null || s.courierBufferHours === '' ? 1 : num(s.courierBufferHours)) * 60);
    var typed = parseMinutes(s.localCutoff);
    var today = dayHours(s, 0, now, 'local');
    var cutoff = today ? today.close - prepMin - bufferMin : null;
    if (typed != null) cutoff = cutoff == null ? typed : Math.min(typed, cutoff);
    if (cutoff != null) { res.cutoffMinutes = cutoff; res.cutoff = fmtMinutes(cutoff); }
    var pr = pauseReason(s, 'local', now);
    if (pr) { res.reason = pr; res.text = pauseText(s, 'local', now); return res; }
    var clean = postalish(postal);
    if (!clean) { res.reason = 'no_postal'; res.text = t('local_prompt'); return res; }
    res.postal = prettyPostal(clean);
    var zone = inZone(clean, s);
    if (zone === null) { res.reason = 'checking'; res.text = t('local_checking'); return res; }
    if (!zone) {
      res.reason = 'outside_zone';
      res.text = t('local_outside', { postal: res.postal, store: s.name });
      return res;
    }
    res.ok = true;
    var nowMin = nowParts(now).minutes;
    if (today && cutoff != null && nowMin < cutoff) {
      res.day = dayLabel(0, today.weekday, false);
      res.dayKey = today.key;
      res.when = t('local_when_today');
      res.text = t('local_today', { cutoff: res.cutoff });
      return res;
    }
    for (var offset = 1; offset <= 14; offset += 1) {
      var day = dayHours(s, offset, now, 'local');
      if (!day) continue;
      var ready = day.open + prepMin;
      if (ready + bufferMin > day.close) continue; // the courier buffer must still fit that day
      res.day = dayLabel(offset, day.weekday, false);
      res.dayKey = day.key;
      res.when = res.day;
      res.minutes = ready;
      res.time = fmtMinutes(ready);
      res.text = t('local_next', { day: res.day, time: res.time });
      return res;
    }
    return res;
  }

  /* ------------------------------------------------------------------ *
   * aside — lines set aside by a service switch (sessionStorage 'paex:aside')
   * ------------------------------------------------------------------ */
  function asideRead() {
    try { var list = JSON.parse(sessionStorage.getItem(ASIDE_KEY) || '[]'); return Array.isArray(list) ? list : []; } catch (error) { return []; }
  }
  function asideWrite(list) {
    try { if (list.length) sessionStorage.setItem(ASIDE_KEY, JSON.stringify(list)); else sessionStorage.removeItem(ASIDE_KEY); } catch (error) { /* storage-less */ }
  }
  var aside = {
    // one row per variant on read as well (a list written before the add-side dedupe can hold two keys of one variant)
    list: function () {
      var seen = {};
      return asideRead().slice().reverse().filter(function (l) { var k = l.variantId ? 'v' + l.variantId : 'k' + l.key; if (seen[k]) return false; seen[k] = true; return true; }).reverse();
    },
    add: function (line) {
      if (!line || !line.key) return asideRead();
      // one row per variant: a line re-added later carries a new key (properties) but is the same item to the shopper
      var list = asideRead().filter(function (l) { return l.key !== line.key && !(line.variantId && String(l.variantId) === String(line.variantId)); });
      list.push(Object.assign({ t: Date.now() }, line));
      asideWrite(list);
      return list;
    },
    remove: function (key) {
      var list = asideRead().filter(function (l) { return l.key !== key; });
      asideWrite(list);
      return list;
    },
    clear: function () { asideWrite([]); },
  };

  /* ------------------------------------------------------------------ *
   * PDP form capture listener (seams §1 step 3)
   * ------------------------------------------------------------------ */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches(FORM_SELECTOR)) return;
    if (form.dataset.paexFulfilOk === '1') { // pass-through after a successful gate
      delete form.dataset.paexFulfilOk;
      return;
    }
    if (!state.mode && !gated()) return; // today's behaviour: not even preventDefault
    var host = form.closest('product-form');
    if (host && host.isloading) return;
    var btn = form.querySelector('[name="add"], .add-cart-button');
    if (btn && btn.hasAttribute('disabled')) return;
    var idInput = form.querySelector('[name="id"]');
    var id = idInput ? idInput.value : '';
    if (!id) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var qtyInput = form.querySelector('[name="quantity"]');
    var submitter = event.submitter || btn || null;
    var instant = window.PaexCartInstant;
    if (instant && typeof instant.arm === 'function') instant.arm(submitter); // felt latency: the drawer opens under the veil now
    var opts = { source: 'product-form', fulfilSource: 'pdp', form: form, submitter: submitter, sectionId: host ? host.dataset.section : null };
    gate({ id: id, quantity: qtyInput ? num(qtyInput.value) || 1 : 1 }, opts).then(function (res) {
      if (res && res.ok) submitForm(form, submitter);
      // cancelled: veil off; the drawer closes too unless the interception UI claimed the event
      else if (instant && typeof instant.lift === 'function') instant.lift(!(res && res.claimed));
    }, function () {
      submitForm(form, submitter); // a gate failure never blocks the native add
    });
  }, true);

  /* ------------------------------------------------------------------ *
   * UI layer (lane D, 2026-10-22 — Track 3 step 4 + step 7, seams §3.7).
   * Everything the shopper SEES of the mode machinery in the cart: the mode
   * header fill (ready line / ship date / same-day store + day, zone +
   * minimum), the CTA block reasons, the after-add strip, the "Removed from
   * your cart" list with Find similar, the service chooser, the add
   * interception dialog, the dry run, and the cart page banner (C5, 2026-10-23:
   * copy and anatomy per the design export). Shells live
   * in sections/paex-cart-drawer.liquid (strip, aside, the two modal-dialog
   * elements) and sections/paex-cart-fulfil.liquid (banner); the mode header
   * and the store island are Liquid in snippets/paex-cart-details.liquid.
   * Every word comes from paexFulfil.texts (paex.fulfil.*), built with
   * textContent / createElement only. Entry points: paex-cart.js calls
   * ui.sync() after every details swap and on drawer open, ui.openChooser /
   * ui.postalApply from its click dispatcher (ModalDialog stops
   * inner clicks at the drawer); the intercept listener and the banner bind
   * here. With no mode every function returns before touching the DOM.
   * ------------------------------------------------------------------ */
  var ui = (function () {
    var SVCS = ['ship', 'pickup', 'local'];
    var CHOOSER_ID = 'Paex-Fulfil-Chooser';
    var INTERCEPT_ID = 'Paex-Fulfil-Intercept';
    var busy = false; // one switch / add-back at a time

    function drawerEl() { return document.getElementById('Cart-Drawer'); }
    function modal(id) { return document.getElementById(id); }
    function setText(node, text) { if (node && node.textContent !== text) node.textContent = text; }
    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }
    function clear(node) { while (node && node.firstChild) node.removeChild(node.firstChild); }
    function bridge() { return window.PaexCartBridge || null; }
    function isOpen(node) { return Boolean(node && node.hasAttribute('open')); }
    function serviceLabel(svc, s) {
      if (svc === 'pickup') return t('pickup_at', { store: s ? s.name : '' });
      return t('service_' + svc);
    }
    // the store a switch would target: the cart's own location, else the
    // resolved store when the shopper is inside its radius and it participates
    function altStoreId() {
      var loc = locationId();
      if (loc) return loc;
      var p = participates(storeId());
      return p.gated ? p.storeId : null;
    }
    // alternatives(current) → [{ service, mode, store, storeId, paused, pauseReason }]
    // (a paused service is still listed: the chooser shows it greyed with its
    // reason; the interception hides it)
    function alternatives(current) {
      current = current || service();
      var sid = altStoreId();
      var p = sid ? participates(sid) : null;
      var out = [];
      SVCS.forEach(function (svc) {
        if (svc === current) return;
        if (svc === 'ship') { out.push({ service: 'ship', mode: 'ship', store: null, storeId: null, paused: false, pauseReason: null }); return; }
        if (!p || !p.store || !p[svc]) return;
        var pr = pauseReason(p.store, svc);
        out.push({ service: svc, mode: makeMode(svc, sid), store: p.store, storeId: sid, paused: Boolean(pr), pauseReason: pr });
      });
      return out;
    }
    // "Arrives {date}" for a ship cart: the drawer strip's own base estimate
    // (paex-edd.js, same config island), no second engine
    function shipDate(scope) {
      var node = (scope || document).querySelector('[data-paex-cart-edd-data]') || document.querySelector('[data-paex-cart-edd-data]');
      if (!node || typeof paexEddEstimate !== 'function' || typeof paexEddFormatDate !== 'function') return '';
      // the date the EDD strip already shows (base or live, paex-cart.js eddPaint) wins — one date per cart
      var painted = (scope || document).querySelector('[data-edd-arrival-key]') || document.querySelector('[data-edd-arrival-key]');
      var key = painted ? painted.getAttribute('data-edd-arrival-key') : '';
      if (key && /^\d{4}-\d{2}-\d{2}$/.test(key)) {
        try { var live = paexEddFormatDate(new Date(key + 'T12:00:00Z')); if (live) return live; } catch (error) { /* fall through to the base estimate */ }
      }
      var cfg = null;
      try { cfg = JSON.parse(node.textContent); } catch (error) { cfg = null; }
      if (!cfg || !cfg.edd) return '';
      var dest = typeof paexDestRead === 'function' ? paexDestRead() : null;
      var geo = window.paexGeo || null;
      var region = (dest && dest.region) || (geo && geo.regionCode) || '*';
      var est = null;
      try { est = paexEddEstimate(region, cfg.edd); } catch (error) { est = null; }
      if (!est || !est.arrival) return '';
      try { return paexEddFormatDate(est.arrival) || ''; } catch (error) { return ''; }
    }
    function cartTotalFromDom(scope) {
      var node = (scope || document).querySelector('[data-paex-cart-total]');
      var v = node ? Number(node.getAttribute('data-paex-cart-total')) : NaN;
      return Number.isFinite(v) ? v : null;
    }
    // fillTokens(node, template, spans): the template's text with one token
    // rendered as its own element (the ship date in green, design 4.1 / 2.1) —
    // text nodes and the given elements only, never innerHTML
    function fillTokens(node, template, spans) {
      if (!node) return;
      clear(node);
      String(template || '').split(/(\{\w+\})/).forEach(function (piece) {
        var m = piece.match(/^\{(\w+)\}$/);
        if (m && spans && spans[m[1]]) node.appendChild(spans[m[1]]);
        else if (piece) node.appendChild(document.createTextNode(piece));
      });
    }
    function dateSpan(cls, date) {
      var span = el('span', cls, date);
      span.setAttribute('data-fulfil-date', '');
      return span;
    }
    function lowerFirst(s) {
      s = String(s || '');
      return s.charAt(0).toLowerCase() + s.slice(1);
    }

    /* ---- mode header fill (drawer + cart page banner share it) ---- */
    // → { service, ok, reason, local } (local = localLine result for a same-day cart)
    // Design 4.1–4.4 / 4.9: icon · "{service} · {detail}" · Modify, no grey
    // sub-line; [data-fulfil-note] only while the service is paused.
    function fillHeader(head, scope) {
      var svc = head.getAttribute('data-service');
      var loc = head.getAttribute('data-location-id') || locationId();
      var s = loc ? store(loc) : null;
      var textEl = head.querySelector('[data-fulfil-header-text]');
      var note = head.querySelector('[data-fulfil-note]');
      // design 4.9: the cart page banner's one-order sentence follows the service (the drawer has no [data-fulfil-sub])
      var sub = head.querySelector('[data-fulfil-sub]');
      if (sub) setText(sub, t('banner_sub_' + svc));
      var paused = head.hasAttribute('data-paused');
      var res = { service: svc, ok: true, reason: null, local: null };
      var setNote = function (text) {
        if (!note) return;
        setText(note, text || '');
        note.hidden = !text;
      };
      var change = head.querySelector('[data-fulfil-change]');
      if (change) change.hidden = !alternatives(svc).length || !modal(CHOOSER_ID);
      if (paused) {
        // design 4.4: "Pickup at {store} · not available right now" (or the
        // blackout range: "· not available Oct 30–31") and the note "Not
        // available right now. Choose ship to home." — the store's own
        // pause_note wins over the generic note when it is set
        var frag = head.querySelector('[data-fulfil-ready]');
        var pf = s ? pauseFragment(s, svc) : '';
        if (frag && pf) setText(frag, pf);
        var pt = s ? pauseText(s, svc) : '';
        setNote(s && s.pauseNote ? String(s.pauseNote) : (pt ? sentence(t('cart_paused_' + svc, { pause: pt })) : ''));
        var prow = head.querySelector('[data-fulfil-postal-row]');
        if (prow) prow.hidden = true;
        res.ok = false;
        res.reason = 'paused';
        return res;
      }
      if (svc === 'pickup') {
        var ready = head.querySelector('[data-fulfil-ready]');
        var r = readyLine(s || {});
        // design 4.2: "Pickup at {store} · ready in 2 h" / "· ready tomorrow
        // around noon" (readyLine.inline, the one source) — and nothing under it (CHANGES 20)
        if (ready && s && s.hours && r.inline) setText(ready, r.inline);
        // the pickup minimum (Sasha 2026-10-24): "Add 24 $ to reach the 30 $ minimum for pickup", checkout disabled below it
        var pmin = s ? moneyCents(s.localMin) : null; // the shared minimum (local_delivery_minimum)
        var ptotal = cartTotalFromDom(scope);
        if (pmin != null && pmin > 0 && ptotal != null && ptotal < pmin) {
          res.ok = false;
          res.reason = 'under_min';
          res.text = underMinText('pickup', pmin - ptotal, pmin);
          setNote(res.text);
        } else {
          setNote('');
        }
      } else if (svc === 'ship') {
        var date = shipDate(scope);
        if (date) fillTokens(textEl, t('header_ship'), { date: dateSpan('paex-cart__fulfil-date', date) });
        else setText(textEl, t('header_ship_nodate'));
        // Sasha 2026-10-23 (option 1): the header carries the postal after the date ("· G2K 0N2", light) and the
        // EDD strip under it is hidden (paex-cart.css) — the chooser's current-service row changes the postal
        var kp = knownPostal();
        if (kp) {
          var placeEl = el('span', 'paex-cart__fulfil-place', ' · ' + prettyPostal(kp));
          placeEl.setAttribute('data-fulfil-place', '');
          textEl.appendChild(placeEl);
        }
        setNote('');
      } else if (svc === 'local') {
        var ll = s ? localLine(s, knownPostal()) : { ok: false, reason: 'not_participating', text: '' };
        res.local = ll;
        var row = head.querySelector('[data-fulfil-postal-row]');
        if (ll.ok) {
          // design 4.3: "Same-day delivery · {store} · today" — no fee here (CHANGES 14)
          setText(textEl, t('header_local', { store: s.name, when: ll.when }));
          var min = ll.min != null ? ll.min : null;
          var total = cartTotalFromDom(scope);
          if (min != null && total != null && total < min) {
            res.ok = false;
            res.reason = 'under_min';
            res.text = t('local_under_min', { amount: money(min - total), min: money(min) });
            setNote(res.text);
          } else {
            setNote('');
          }
          if (row) row.hidden = true;
        } else {
          setText(textEl, t('service_local'));
          res.ok = false;
          res.reason = ll.reason;
          res.text = ll.reason === 'no_postal' ? t('local_postal_missing') : ll.text;
          setNote(res.text);
          if (row) row.hidden = !(ll.reason === 'no_postal' || ll.reason === 'outside_zone');
        }
      }
      return res;
    }
    // CTA: Liquid printed the base reason (paused / flagged lines) in
    // data-base; the same-day verdict rides on top and clears live
    function syncCta(root, verdict) {
      var cta = root.querySelector('.paex-cart__cta');
      var reason = root.querySelector('[data-fulfil-cta-reason]');
      if (!cta || !reason) return;
      var base = reason.getAttribute('data-base') || '';
      var extra = verdict && (verdict.service === 'local' || verdict.service === 'pickup') && !verdict.ok ? (verdict.text || '') : '';
      var text = base || extra;
      cta.disabled = Boolean(text);
      cta.toggleAttribute('data-fulfil-blocked', Boolean(text));
      if (text) cta.setAttribute('aria-disabled', 'true'); else cta.removeAttribute('aria-disabled');
      setText(reason, text);
      reason.hidden = !text;
    }
    function syncDrawer() {
      var d = drawerEl();
      if (!d) return;
      var head = d.querySelector('#Cart-Drawer-Details [data-fulfil-header]');
      var verdict = head ? fillHeader(head, d) : null;
      syncCta(d, verdict);
      renderAside();
    }
    // sync(): after every details swap (paex-cart.js syncDetails), on drawer
    // open, on paex:dest-change / paex:fulfil-origin, and after a mode change
    function sync() {
      syncDrawer();
      syncBanner();
    }

    /* ---- same-day postal editor in the header ([data-fulfil-postal-check]) ---- */
    function regionOf(postal) {
      var first = String(postal || '').trim().toUpperCase().charAt(0);
      var map = { A: 'NL', B: 'NS', C: 'PE', E: 'NB', G: 'QC', H: 'QC', J: 'QC', K: 'ON', L: 'ON', M: 'ON', N: 'ON', P: 'ON', R: 'MB', S: 'SK', T: 'AB', V: 'BC', X: 'NT', Y: 'YT' };
      return map[first] || null;
    }
    function postalApply(scope) {
      var head = scope && scope.querySelector('[data-fulfil-header]');
      var input = head && head.querySelector('[data-fulfil-postal-input]');
      var error = head && head.querySelector('[data-fulfil-postal-error]');
      if (!input) return;
      var clean = String(input.value || '').replace(/\s+/g, '').toUpperCase();
      if (!/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(clean)) {
        if (error) { setText(error, error.getAttribute('data-error-text') || ''); error.hidden = false; }
        return;
      }
      if (error) error.hidden = true;
      var pretty = prettyPostal(clean);
      if (typeof paexDestWrite === 'function') paexDestWrite({ place: pretty, region: regionOf(clean), postal: pretty }); // → paex:dest-change → sync()
      var b = bridge();
      if (b && typeof b.setAttributes === 'function') {
        var w = b.setAttributes({ edd_postal: pretty }, { drawer: null }); // no repaint while the editor is open
        if (w && typeof w.catch === 'function') w.catch(function () {});
      }
      if (state.cart && state.cart.attributes) state.cart.attributes.edd_postal = pretty;
      resolveOrigin();
      sync();
      var change = head.querySelector('[data-fulfil-change]');
      var focusTo = head.querySelector('[data-fulfil-postal-row]:not([hidden]) [data-fulfil-postal-input]') || (change && !change.hidden ? change : null);
      if (focusTo) focusTo.focus();
    }

    /* ---- after-add strip (strip_pickup / strip_local / strip_ship) ---- */
    // design 2.1 / 2.2 / 2.3 / 5b.3: one sentence per service, nothing else —
    // pickup "Added for pickup at {store} · usually ready in 2 hours." ({ready}
    // = readyLine().text lower-cased; closed "ready tomorrow around noon"; over
    // a pause the short paused fragment, never a ready promise), same-day
    // "Added for same-day delivery from {store}.", ship "Added to cart ·
    // arrives {date}" with the date in green and a Modify link (the chooser)
    function stripText(svc) {
      var loc = locationId();
      var s = loc ? store(loc) : null;
      if (svc === 'pickup') {
        var r = readyLine(s || {});
        var ready = s && pausedToday(s, 'pickup') ? pauseFragment(s, 'pickup') : lowerFirst(r.text);
        return t('strip_pickup', { store: s ? s.name : storeName(loc), ready: ready });
      }
      if (svc === 'local') {
        var ll = s ? localLine(s, knownPostal()) : null;
        return ll && ll.ok ? t('strip_local', { store: s.name }) : '';
      }
      return shipDate(drawerEl()) ? t('strip_ship', { date: '{date}' }) : '';
    }
    function showStrip() {
      var d = drawerEl();
      var node = d && d.querySelector('[data-fulfil-strip]');
      if (!node) return;
      var svc = service();
      var text = svc ? stripText(svc) : '';
      if (!text) { hideStrip(); return; }
      // the shell keeps its check icon (sections/paex-cart-drawer.liquid); only
      // the text span is rebuilt — an older shell without it takes the whole node
      var target = node.querySelector('[data-fulfil-strip-text]') || node;
      if (svc === 'ship') fillTokens(target, text, { date: dateSpan('paex-cart__fstrip-date', shipDate(d)) });
      else { clear(target); target.appendChild(document.createTextNode(text)); }
      // design 5b.3: the ship strip carries its own "Modify" (opens the chooser —
      // paex-cart.js onDrawerClick routes every [data-fulfil-change] in the drawer)
      if (svc === 'ship' && alternatives(svc).length && modal(CHOOSER_ID)) {
        target.appendChild(document.createTextNode(' '));
        var change = el('button', 'paex-cart__fstrip-change', t('change_service'));
        change.type = 'button';
        change.setAttribute('data-fulfil-change', '');
        change.setAttribute('aria-haspopup', 'dialog');
        change.setAttribute('aria-controls', CHOOSER_ID);
        target.appendChild(change);
      }
      node.setAttribute('data-service', svc);
      node.hidden = false;
      d.setAttribute('data-fulfil-strip-open', '');
    }
    function hideStrip() {
      var d = drawerEl();
      var node = d && d.querySelector('[data-fulfil-strip]');
      if (!node) return;
      node.hidden = true;
      node.removeAttribute('data-service');
      d.removeAttribute('data-fulfil-strip-open');
    }
    // cartUpdate: an ADD answers with the line item(s) (no item_count) — a
    // change / update answers with the whole cart (seams §2.10)
    function onCartUpdate(payload) {
      var data = payload && payload.cartData;
      if (!data || typeof data !== 'object' || data.status) return;
      var isAdd = (typeof data.key === 'string' && data.item_count === undefined)
        || (Array.isArray(data.items) && data.item_count === undefined);
      if (!state.mode) return; // no mode: nothing to show, nothing to re-read
      if (isAdd) showStrip();
      reloadBanner(); // the cart page re-reads its banner (fresh totals / lines)
    }

    /* ---- dialogs (modal-dialog shells): title / sub / body / actions ---- */
    // the element focus returns to on close: while the drawer is open it must
    // be INSIDE the drawer (hide() re-arms the drawer's own focus trap through
    // it); otherwise the control that opened the dialog
    function openerFor(opener) {
      var d = drawerEl();
      if (d && isOpen(d)) {
        if (opener && opener.isConnected && d.contains(opener)) return opener;
        return d.querySelector('#Cart-Drawer-Details [data-fulfil-change]:not([hidden])') || d.querySelector('.drawer-close');
      }
      if (opener && opener.isConnected) return opener;
      var active = document.activeElement;
      return active && active !== document.body ? active : null;
    }
    function openDialog(id, spec, opener) {
      var m = modal(id);
      if (!m || typeof m.show !== 'function') return null;
      var title = m.querySelector('[data-dialog-title]');
      var sub = m.querySelector('[data-dialog-sub]');
      var body = m.querySelector('[data-dialog-body]');
      var iconBox = m.querySelector('[data-dialog-icon]');
      setText(title, spec.title || '');
      if (sub) { setText(sub, spec.sub || ''); sub.hidden = !spec.sub; }
      if (iconBox) {
        // the title icon (checklist 2 / rule 3.4-5): the theme's warning glyph in
        // an amber circle ('warn') when an item cannot follow the order or will be
        // removed; a neutral question-mark circle ('ask') when the shopper only
        // has to choose — <template data-fulfil-icon> in sections/paex-cart-drawer.liquid
        clear(iconBox);
        var tpl = spec.icon ? document.querySelector('template[data-fulfil-icon="' + spec.icon + '"]') : null;
        if (tpl && tpl.content) { iconBox.appendChild(tpl.content.cloneNode(true)); iconBox.setAttribute('data-icon', spec.icon); iconBox.hidden = false; }
        else { iconBox.removeAttribute('data-icon'); iconBox.hidden = true; }
      }
      clear(body);
      if (spec.text) body.appendChild(el('p', 'paex-fulfil-modal__text', spec.text));
      if (spec.consequence) body.appendChild(el('p', 'paex-fulfil-modal__consequence', spec.consequence));
      if (spec.content) body.appendChild(spec.content);
      if (spec.actions && spec.actions.length) {
        var row = el('div', 'paex-fulfil-modal__actions');
        spec.actions.forEach(function (a) {
          var b = el('button', 'paex-fulfil-modal__btn' + (a.primary ? ' paex-fulfil-modal__btn--primary' : ''), a.label);
          b.type = 'button';
          if (a.attrs) Object.keys(a.attrs).forEach(function (k) { b.setAttribute(k, a.attrs[k]); });
          b.addEventListener('click', function (event) { event.preventDefault(); a.onClick(b, m); });
          row.appendChild(b);
        });
        body.appendChild(row);
      }
      if (spec.dryrun) m.setAttribute('data-fulfil-dryrun', ''); else m.removeAttribute('data-fulfil-dryrun');
      if (!isOpen(m)) m.show(openerFor(opener));
      else if (typeof webvista !== 'undefined' && webvista.trapFocus) webvista.trapFocus(m);
      return m;
    }
    function closeDialog(id) {
      var m = modal(id);
      if (m && isOpen(m)) m.hide();
    }
    function setBusy(node, on) {
      if (!node) return;
      node.classList.toggle('is-busy', Boolean(on));
      node.setAttribute('aria-busy', on ? 'true' : 'false');
      if (node.tagName === 'BUTTON') node.disabled = Boolean(on);
    }

    /* ---- the switch: dry run → lines moved to the aside → set(target) ---- */
    // doSwitch(target, res, source): res = checkCart(target) already run;
    // the order (aside first, write second) is the core's switchCart — the
    // Validation Function runs on cart/update.js (gate fix 2026-10-23)
    // refreshPageCart(): the cart PAGE lists its lines server-side (cart-items
    // outside the drawer); a switch / Add back changes lines through the
    // bridge with drawer sections only, so the page list is re-rendered here
    // (seen on the preview Oct 22: the set-aside robe stayed listed on /cart)
    function refreshPageCart() {
      var ci = document.querySelector('cart-items:not([data-in-drawer])');
      var b = bridge();
      if (!ci || !b || typeof b.refreshSections !== 'function' || typeof ci.getSectionsToRender !== 'function') return;
      try { var p = b.refreshSections({ sections: ci.getSectionsToRender(), drawer: null }); if (p && p.catch) p.catch(function () {}); } catch (error) { /* the page keeps its list */ }
    }
    function doSwitch(target, res, source) {
      return switchCart(target, res, { source: source || 'chooser' }).then(function () {
        renderAside();
        sync();
        refreshPageCart();
      });
    }
    // askSwitch(target, opener, source) → Promise<boolean switched>:
    // checkCart → every line fits (0 removed) → the switch runs at once, no
    // dialog (design 4.5 / plan rule 2); K ≥ 1 lines would be removed → the
    // dry run (4.6): "Ship your whole order?" (switch_q_*, warning icon),
    // "Removed: {names}.", then "Yes, change it" / "Keep it as is" — Keep
    // returns to the chooser when the chooser opened it
    function askSwitch(target, opener, source) {
      if (busy) return Promise.resolve(false);
      busy = true;
      return checkCart(target).then(function (res) {
        if (res.cartReason) { busy = false; return false; } // not viable (paused / zone / minimum): the chooser already says so
        var svc = service(target);
        var n = distinct(res.unavailable).length;
        if (!n) {
          return doSwitch(target, res, source).then(function () { return true; }, function () { renderAside(); sync(); refreshPageCart(); return false; });
        }
        return new Promise(function (resolve) {
          var back = function () { if (source === 'chooser') openChooser(opener); };
          var spec = {
            title: t('switch_q_' + svc),
            text: dryrunText(target, res),
            icon: 'warn',
            dryrun: true,
            actions: [
              { label: t('dryrun_yes'), primary: true, attrs: { 'data-fulfil-continue': '' }, onClick: function (b, m) {
                setBusy(b, true);
                // a failed write (422: a line came back, e.g. an app re-added a gift) still removed lines → the page list must tell the truth
                doSwitch(target, res, source).then(function () { m.hide(); resolve(true); }, function () { setBusy(b, false); renderAside(); sync(); refreshPageCart(); resolve(false); });
              } },
              { label: t('dryrun_keep'), attrs: { 'data-fulfil-cancel': '' }, onClick: function (b, m) { m.hide(); resolve(false); back(); } },
            ],
          };
          var m = openDialog(INTERCEPT_ID, spec, opener);
          if (!m) { resolve(false); return; }
          // Esc / overlay close = keep it as is (a Yes resolved first; a second resolve is a no-op)
          var obs = new MutationObserver(function () { if (!isOpen(m)) { obs.disconnect(); resolve(false); } });
          obs.observe(m, { attributes: true, attributeFilter: ['open'] });
        });
      }).then(function (switched) { busy = false; return switched; }, function () { busy = false; return false; });
    }

    /* ---- the chooser (#Paex-Fulfil-Chooser) ---- */
    // the service icon (truck / store / van) printed as a <template
    // data-fulfil-icon> inside the chooser shell (sections/paex-cart-drawer.liquid)
    function serviceIcon(svc) {
      var m = modal(CHOOSER_ID);
      var tpl = m && m.querySelector('template[data-fulfil-icon="' + svc + '"]');
      var box = el('span', 'paex-fulfil-opt__icon');
      box.setAttribute('aria-hidden', 'true');
      if (tpl && tpl.content) box.appendChild(tpl.content.cloneNode(true));
      return box;
    }
    // a row that cannot be picked (paused / no postal / outside the zone /
    // under the minimum): the UNAVAILABLE treatment (grey dot, muted text,
    // aria-disabled on the row) — the same-day postal editor under it stays live
    function disableOption(wrap, btn, state) {
      wrap.setAttribute('data-state', state);
      wrap.setAttribute('aria-disabled', 'true');
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
    }
    function optionNode(alt) {
      var wrap = el('div', 'paex-fulfil-opt');
      wrap.setAttribute('data-chooser-option', '');
      wrap.setAttribute('data-service', alt.service);
      wrap.setAttribute('data-state', alt.paused ? 'paused' : 'checking');
      var btn = el('button', 'paex-fulfil-opt__btn');
      btn.type = 'button';
      btn.setAttribute('data-chooser-pick', '');
      btn.appendChild(serviceIcon(alt.service));
      var textBox = el('span', 'paex-fulfil-opt__text');
      textBox.appendChild(el('span', 'paex-fulfil-opt__label', serviceLabel(alt.service, alt.store)));
      var status = el('span', 'paex-fulfil-opt__status');
      status.appendChild(el('span', 'paex-fulfil-opt__dot'));
      var sub = el('span', 'paex-fulfil-opt__sub');
      sub.setAttribute('data-chooser-sub', '');
      status.appendChild(sub);
      textBox.appendChild(status);
      btn.appendChild(textBox);
      wrap.appendChild(btn);
      if (alt.paused) {
        setText(sub, pauseText(alt.store, alt.service)); // "Not available right now" / "Not available Oct 30–31"
        disableOption(wrap, btn, 'paused');
      }
      return wrap;
    }
    /* ---- the shared postal sheet (Sasha 2026-10-23): one editor everywhere — the BOPIS sheet
       (#PaexFulfilPostalSheetTpl: the clock sheet's markup + CSS, neutral copy, never a word about Halloween). postalSheet(trigger) → true when a sheet
       opened, false when none exists (callers keep their inline editor as the fallback). A full postal typed
       in either sheet is written with paexDestWrite (→ paex:dest-change: every surface re-renders) and
       mirrored into the cart attribute edd_postal (mirrorPostal) so the cart's first paint agrees. ---- */
    var fsheet = null, fsheetTrigger = null, fsheetScrollLocked = false, fsheetCssReady = null;
    // Sasha 2026-10-23: never the clock's sheet — nothing about Halloween in the fulfilment messaging
    function sheetAvailable() {
      return Boolean(document.getElementById('PaexFulfilPostalSheetTpl'));
    }
    function loadSheetCss(tpl) {
      if (!fsheetCssReady) {
        fsheetCssReady = new Promise(function (resolve) {
          if (!tpl.dataset.css || document.querySelector('link[data-paex-postal-css]')) { resolve(); return; }
          var link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = tpl.dataset.css;
          link.setAttribute('data-paex-postal-css', '');
          link.onload = link.onerror = function () { resolve(); };
          document.head.appendChild(link);
          setTimeout(resolve, 3000);
        });
      }
      return fsheetCssReady;
    }
    function closeFulfilSheet() {
      if (!fsheet || fsheet.hidden) return;
      fsheet.hidden = true;
      var wv = typeof webvista !== 'undefined' ? webvista : null;
      if (fsheetScrollLocked && wv && typeof wv.enablePageScroll === 'function') wv.enablePageScroll();
      fsheetScrollLocked = false;
      if (wv && typeof wv.removeTrapFocus === 'function') wv.removeTrapFocus(fsheetTrigger);
      else if (fsheetTrigger && fsheetTrigger.focus) fsheetTrigger.focus();
    }
    function ensureFulfilSheet() {
      if (fsheet) return fsheet;
      var tpl = document.getElementById('PaexFulfilPostalSheetTpl');
      if (!tpl) return null;
      document.body.appendChild(tpl.content.cloneNode(true));
      fsheet = document.querySelector('[data-paex-fulfil-sheet]');
      var form = fsheet.querySelector('[data-pps-form]');
      var input = fsheet.querySelector('[data-pps-input]');
      var error = fsheet.querySelector('[data-pps-error]');
      fsheet.querySelectorAll('[data-pps-close]').forEach(function (b) { b.addEventListener('click', closeFulfilSheet); });
      fsheet.addEventListener('keyup', function (event) { if (event.key === 'Escape') { event.stopPropagation(); closeFulfilSheet(); } });
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var clean = String(input.value || '').replace(/\s+/g, '').toUpperCase();
        if (!/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(clean) || !regionOf(clean)) {
          input.setAttribute('aria-invalid', 'true');
          setText(error, t('local_postal_error'));
          error.hidden = false;
          input.focus();
          return;
        }
        input.removeAttribute('aria-invalid');
        error.hidden = true;
        var pretty = prettyPostal(clean);
        var dest = { place: pretty, region: regionOf(clean), postal: pretty };
        var km = typeof paexPostalKeys === 'function' ? paexPostalKeys(clean) : null;
        var table = fsaTableSync();
        if (km && table) { for (var i = 0; i < km.length; i += 1) { var hit = table[km[i]]; if (hit && isFinite(hit[0]) && isFinite(hit[1])) { dest.lat = Number(hit[0]); dest.lng = Number(hit[1]); break; } } }
        if (typeof paexDestWrite === 'function') paexDestWrite(dest);
        closeFulfilSheet();
      });
      return fsheet;
    }
    function showFulfilSheet(trigger) {
      var element = ensureFulfilSheet();
      if (!element) return;
      fsheetTrigger = trigger;
      var panel = element.querySelector('[data-pps-panel]');
      var input = element.querySelector('[data-pps-input]');
      var error = element.querySelector('[data-pps-error]');
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      var known = knownPostal();
      input.value = known && /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(known) ? prettyPostal(known) : '';
      element.hidden = false;
      var desktop = window.matchMedia && window.matchMedia('(min-width: 750px)').matches;
      if (desktop && fsheetTrigger && fsheetTrigger.getBoundingClientRect) {
        var rect = fsheetTrigger.getBoundingClientRect();
        panel.style.top = Math.round(rect.bottom + 8) + 'px';
        panel.style.left = Math.round(Math.min(Math.max(12, rect.left + rect.width / 2 - 180), window.innerWidth - 372)) + 'px';
      } else {
        panel.style.top = '';
        panel.style.left = '';
      }
      var wv = typeof webvista !== 'undefined' ? webvista : null;
      if (wv && typeof wv.trapFocus === 'function') wv.trapFocus(panel, input);
      else input.focus({ preventScroll: true });
      if (wv && typeof wv.disablePageScroll === 'function' && !desktop) { wv.disablePageScroll(); fsheetScrollLocked = true; }
    }
    function postalSheet(trigger) {
      var tpl = document.getElementById('PaexFulfilPostalSheetTpl');
      if (!tpl) return false;
      loadSheetCss(tpl).then(function () { showFulfilSheet(trigger || document.activeElement); });
      return true;
    }
    // every postal write (either sheet, the PDP editors, the shipping page) lands in the cart attribute too
    function mirrorPostal(dest) {
      var p = dest ? postalish(dest.postal || dest.place) : null;
      if (!p || !/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(p)) return;
      var pretty = prettyPostal(p);
      var cur = state.cart && state.cart.attributes ? String(state.cart.attributes.edd_postal || '').replace(/\s+/g, '').toUpperCase() : '';
      if (cur === p) return;
      var b = bridge();
      if (b && typeof b.setAttributes === 'function') { var w = b.setAttributes({ edd_postal: pretty }, { drawer: null }); if (w && w.catch) w.catch(function () {}); }
      if (state.cart && state.cart.attributes) state.cart.attributes.edd_postal = pretty;
    }
    // the drawer header's same-day postal button + any [data-fulfil-postal-open]
    document.addEventListener('click', function (event) {
      var b = event.target && event.target.closest ? event.target.closest('[data-fulfil-postal-open]') : null;
      if (!b) return;
      event.preventDefault();
      postalSheet(b);
    });
    // the same-day option carries the postal input (never a disabled row)
    function postalBox(chooser, onApply) {
      var box = el('div', 'paex-cart__edd-editor paex-fulfil-opt__postal');
      var input = el('input', 'paex-cart__edd-input');
      input.type = 'text';
      input.setAttribute('data-chooser-postal', '');
      input.placeholder = chooser.getAttribute('data-postal-placeholder') || '';
      input.setAttribute('aria-label', input.placeholder);
      input.autocomplete = 'postal-code';
      input.setAttribute('autocapitalize', 'characters');
      // design 7.1: prefill only a full postal code the shopper typed (an FSA-only
      // geo / destination value would fail the check below), else the placeholder
      var known = knownPostal();
      if (known && /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(known)) input.value = prettyPostal(known);
      // the button label comes from the fulfil strings (« Vérifier »), the drawer's EDD section setting only as a fallback
      var check = el('button', 'paex-cart__edd-check', t('local_check') || chooser.getAttribute('data-check-label') || '');
      check.type = 'button';
      check.setAttribute('data-chooser-postal-check', '');
      var error = el('p', 'paex-cart__edd-error');
      error.setAttribute('role', 'alert');
      error.hidden = true;
      var apply = function () {
        var clean = String(input.value || '').replace(/\s+/g, '').toUpperCase();
        if (!/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(clean)) { setText(error, chooser.getAttribute('data-postal-error') || ''); error.hidden = false; return; }
        error.hidden = true;
        var pretty = prettyPostal(clean);
        if (typeof paexDestWrite === 'function') paexDestWrite({ place: pretty, region: regionOf(clean), postal: pretty });
        var b = bridge();
        if (b && typeof b.setAttributes === 'function') { var w = b.setAttributes({ edd_postal: pretty }, { drawer: null }); if (w && w.catch) w.catch(function () {}); }
        if (state.cart && state.cart.attributes) state.cart.attributes.edd_postal = pretty;
        resolveOrigin();
        onApply();
      };
      check.addEventListener('click', apply);
      input.addEventListener('keydown', function (event) { if (event.key === 'Enter') { event.preventDefault(); apply(); } });
      box.appendChild(input);
      box.appendChild(check);
      var frag = document.createDocumentFragment();
      frag.appendChild(box);
      frag.appendChild(error);
      return frag;
    }
    function fillOption(wrap, alt, opener) {
      var sub = wrap.querySelector('[data-chooser-sub]');
      var btn = wrap.querySelector('[data-chooser-pick]');
      if (alt.paused) return;
      checkCart(alt.mode).then(function (res) {
        if (!wrap.isConnected) return;
        var n = distinct(res.unavailable).length;
        if (res.cartReason && alt.service === 'local' && (res.cartReason === 'no_postal' || res.cartReason === 'outside_zone')) {
          // findings 19 / 24: outside the zone is UNAVAILABLE (grey), never the amber
          // tier; without a postal the reason is the prompt (design 4.5)
          setText(sub, res.cartReason === 'no_postal' ? t('local_prompt') : (res.local && res.local.text) || '');
          disableOption(wrap, btn, 'unavailable');
          if (!wrap.querySelector('[data-chooser-postal]') && !wrap.querySelector('[data-chooser-postal-open]')) {
            if (sheetAvailable()) {
              var holder = el('div', 'paex-fulfil-opt__postalbtn');
              var openBtn = el('button', 'paex-fulfil-opt__modify', t('local_prompt'));
              openBtn.type = 'button';
              openBtn.setAttribute('data-chooser-postal-open', '');
              openBtn.addEventListener('click', function () { postalSheet(openBtn); });
              holder.appendChild(openBtn);
              wrap.appendChild(holder);
            } else {
              wrap.appendChild(postalBox(modal(CHOOSER_ID), function () { fillOption(wrap, alt, opener); }));
            }
          }
          return;
        }

        if (res.cartReason) {
          setText(sub, pauseText(alt.store, alt.service) || t('paused_now'));
          disableOption(wrap, btn, 'paused');
          return;
        }
        var postalRow = wrap.querySelector('.paex-fulfil-opt__postal');
        if (postalRow) { postalRow.remove(); var err = wrap.querySelector('.paex-cart__edd-error'); if (err) err.remove(); }
        var postalBtn = wrap.querySelector('.paex-fulfil-opt__postalbtn');
        if (postalBtn) postalBtn.remove();
        wrap.setAttribute('data-state', n ? 'partial' : 'ok');
        wrap.removeAttribute('aria-disabled');
        // design 4.5: "All your items can be shipped" / "{k} items can’t be shipped: {names}"
        // under the minimum the option stays selectable: the sub says what is missing, the header note + the held
        // checkout button say it again after the switch
        setText(sub, n ? plural('some_unavailable_' + alt.service, n, { k: n, names: names(res.unavailable) }) : (res.underMin ? res.underMin.text : t('all_available_' + alt.service)));
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        btn.onclick = function () {
          closeDialog(CHOOSER_ID);
          askSwitch(alt.mode, opener, 'chooser');
        };
      });
    }
    // openChooser(opener): design 4.5 — "How do you want to get your order?",
    // "Your whole order will use the option you pick.", one row per alternative
    // with its availability line, Cancel. Modify ALWAYS opens it (the earlier
    // one-alternative shortcut to the dry run is retired: the dry run's "Keep
    // it as is" returns here).
    // the CURRENT service as the first row of the chooser (Sasha 2026-10-23, option 1): not selectable, a
    // "Current" tag, its own line — ship: "Arrives {date} · G2K 0N2 · Modify" where Modify opens the postal box
    // (the header's postal follows); pickup: the ready sentence; same-day: the cutoff / day sentence
    function currentNode(svc, opener) {
      var chooser = modal(CHOOSER_ID);
      var s = store(locationId());
      var wrap = el('div', 'paex-fulfil-opt');
      wrap.setAttribute('data-chooser-current', '');
      wrap.setAttribute('data-service', svc);
      wrap.setAttribute('data-state', 'current');
      var row = el('div', 'paex-fulfil-opt__btn paex-fulfil-opt__row');
      row.appendChild(serviceIcon(svc));
      var textBox = el('span', 'paex-fulfil-opt__text');
      var label = el('span', 'paex-fulfil-opt__label', serviceLabel(svc, s));
      var tag = el('span', 'paex-fulfil-opt__tag', t('chooser_current'));
      label.appendChild(document.createTextNode(' '));
      label.appendChild(tag);
      textBox.appendChild(label);
      var status = el('span', 'paex-fulfil-opt__status');
      status.appendChild(el('span', 'paex-fulfil-opt__dot'));
      var sub = el('span', 'paex-fulfil-opt__sub');
      sub.setAttribute('data-chooser-current-sub', '');
      status.appendChild(sub);
      textBox.appendChild(status);
      row.appendChild(textBox);
      wrap.appendChild(row);
      var paint = function () {
        while (sub.firstChild) sub.removeChild(sub.firstChild);
        if (svc === 'ship') {
          // the same date the header that opened the chooser shows (the drawer's strip, or the page's)
          var dateScope = (opener && opener.closest && (opener.closest('#Cart-Drawer') || opener.closest('[data-paex-cart-fulfil]'))) || drawerEl();
          var date = shipDate(dateScope);
          if (date) sub.appendChild(document.createTextNode(t('ship_arrives', { date: date })));
          var kp = knownPostal();
          if (kp) {
            var place = el('span', 'paex-fulfil-opt__place', (date ? ' · ' : '') + prettyPostal(kp));
            place.setAttribute('data-chooser-place', '');
            sub.appendChild(place);
          }
          var modify = el('button', 'paex-fulfil-opt__modify', kp ? t('dest_modify') : t('local_prompt'));
          modify.type = 'button';
          modify.setAttribute('data-chooser-postal-open', '');
          modify.addEventListener('click', function () {
            if (postalSheet(modify)) return; // the shared sheet; the inline box below only when no sheet exists
            var open = wrap.querySelector('[data-chooser-postal]');
            if (open) { var box = wrap.querySelector('.paex-fulfil-opt__postal'); if (box) box.remove(); var err = wrap.querySelector('.paex-cart__edd-error'); if (err) err.remove(); return; }
            wrap.appendChild(postalBox(chooser, function () {
              var box2 = wrap.querySelector('.paex-fulfil-opt__postal'); if (box2) box2.remove();
              var err2 = wrap.querySelector('.paex-cart__edd-error'); if (err2) err2.remove();
              paint();
              sync();
            }));
            var input = wrap.querySelector('[data-chooser-postal]');
            if (input) input.focus();
          });
          sub.appendChild(document.createTextNode(kp ? ' · ' : ' '));
          sub.appendChild(modify);
        } else if (svc === 'pickup') {
          var r = readyLine(s || {});
          setText(sub, s && s.hours && r.text ? r.text : '');
        } else {
          var ll = s ? localLine(s, knownPostal()) : null;
          setText(sub, ll ? ll.text : '');
        }
      };
      paint();
      return wrap;
    }
    function openChooser(opener) {
      var alts = alternatives(service());
      if (!alts.length) return;
      var chooser = modal(CHOOSER_ID);
      if (!chooser) return;
      var list = el('div', 'paex-fulfil-opts');
      list.setAttribute('role', 'group');
      list.appendChild(currentNode(service(), opener));
      alts.forEach(function (alt) {
        var node = optionNode(alt);
        list.appendChild(node);
        fillOption(node, alt, opener);
      });
      var m = openDialog(CHOOSER_ID, {
        title: t('chooser_title'),
        sub: t('chooser_sub'),
        content: list,
        actions: [], // design 4.5: no Cancel button — the X / Esc / overlay close the chooser
      }, opener);
      // a postal typed in the sheet while the chooser is open: rebuild its rows (the current line, the same-day verdict)
      if (m) {
        var onDest = function () {
          document.removeEventListener('paex:dest-change', onDest);
          if (isOpen(m)) setTimeout(function () { if (isOpen(m)) openChooser(opener); }, 60);
        };
        document.addEventListener('paex:dest-change', onDest);
      }
    }

    /* ---- the interception (paex:fulfil-intercept, cancelable) ---- */
    // openDryrun(d, opener): the dry run for an add that would switch the
    // cart's service (d.cartCheck = checkCart(wanted), d.proceed = the core's
    // aside → set → retry chain)
    function openDryrun(d, opener, copy) {
      var cartSvc = service(d.cartMode);
      var wantedSvc = service(d.wanted);
      var items = normalizeItems(d.payload);
      var effective = cartSvc || 'ship'; // a mode-less cart ships
      copy = copy || interceptCopy(d);
      // design 4.6: "Pick up your whole order in store?" · "Removed: {names}." ·
      // Yes, change it / Keep it as is — warning icon
      var spec = {
        title: copy.title,
        text: copy.text,
        icon: 'warn',
        dryrun: true,
        actions: [],
      };
      var run = function (b, m) {
        if (busy) return;
        busy = true;
        setBusy(b, true);
        d.proceed().then(function (res) {
          busy = false;
          renderAside();
          sync();
          refreshPageCart();
          // the retried add was intercepted again (the dialog now shows it) or
          // a 422 replaced the body with the Function's message: it stays
          if (res && res.intercepted && res.claimed) return;
          if (isOpen(m) && m.querySelector('[data-fulfil-continue]') === b) m.hide();
        }, function () {
          busy = false;
          setBusy(b, false);
          renderAside();
          sync();
          refreshPageCart(); // lines were removed before the write failed
        });
      };
      spec.actions.push({ label: t('dryrun_yes'), primary: true, attrs: { 'data-fulfil-continue': '' }, onClick: run });
      var keep = { label: t('dryrun_keep'), attrs: { 'data-intercept-keep': '' }, onClick: function (b, m) { m.hide(); } };
      var open = function () {
        spec.actions.push(keep);
        openDialog(INTERCEPT_ID, spec, opener);
      };
      // the middle option: add in the cart's effective service when the item exists for it
      var alt = alternatives(wantedSvc).filter(function (a) { return a.service === effective && !a.paused; })[0];
      if (!alt || !items.length || effective === wantedSvc) { open(); return; }
      Promise.all(items.map(function (it) { return check(it.id, alt.mode, it.quantity, d.opts || {}); })).then(function (checks) {
        if (checks.every(function (x) { return x.ok; })) {
          spec.actions.push({ label: t('intercept_add_' + effective), attrs: { 'data-intercept-add': '', 'data-service': effective }, onClick: function (b, m) {
            setBusy(b, true);
            m.hide();
            d.retry(alt.mode);
          } });
        }
        open();
      }, open);
    }
    // onIntercept: design 3.1 / 3.2 / 5b.5 — the title says what happens in
    // customer words, one sentence says why, the buttons name the result:
    //  3.1 (the item cannot follow the cart): "Keep my order as is" (primary,
    //      first: nothing is added) · "Ship my whole order instead" (switches
    //      the cart, removing the lines the consequence box names, then adds)
    //  3.2 (the item fits the cart): "Add to my shipped order" (primary) ·
    //      "Pick up my whole order instead"; no third Cancel — the X closes
    //  no cart service yet: the switch the item exists for leads, Cancel closes
    function onIntercept(event) {
      var d = event.detail;
      if (!d || !modal(INTERCEPT_ID)) return; // no shell on this page: the core's toast backstop runs
      event.preventDefault();
      var cartSvc = service(d.cartMode);
      var c = d.check || {};
      var wantedSvc = service(d.wanted);
      var opener = (d.opts && d.opts.submitter) || null;
      var copy = interceptCopy(d);
      // the Function refused the write (422) and our check names no line (a
      // line our stock view does not know): its own sentence, "Keep my order as is"
      if (d.kind === 'blocked') {
        openDialog(INTERCEPT_ID, {
          title: copy.title,
          text: copy.text,
          icon: copy.icon,
          actions: [{ label: t('keep_cart'), primary: true, attrs: { 'data-intercept-keep': '' }, onClick: function (b, m) { m.hide(); } }],
        }, opener);
        return;
      }
      // a cart with lines takes the wanted service only after the dry run
      // (gate fix 2026-10-23): Yes = remove → mode → the add again
      if (d.kind === 'conflict' && d.cartCheck && d.cartCheck.unavailable.length && typeof d.proceed === 'function') {
        openDryrun(d, opener, copy);
        return;
      }
      var missing = c.ok === false; // the item does not exist for the cart's service
      var leads = [];
      var switches = [];
      var keep = { label: cartSvc ? t('keep_cart') : t('cancel'), attrs: { 'data-intercept-keep': '' }, onClick: function (b, m) { m.hide(); } };
      if (cartSvc && !missing) {
        leads.push({ label: t('intercept_keep_' + cartSvc), primary: true, attrs: { 'data-intercept-add': '', 'data-service': cartSvc }, onClick: function (b, m) {
          setBusy(b, true);
          m.hide();
          d.retry();
        } });
      } else if (cartSvc) {
        keep.primary = true;
        leads.push(keep);
      }
      // the switch: the wanted service (conflict), else every alternative the item exists for
      var candidates = [];
      var alts = alternatives(cartSvc || null);
      if (d.kind === 'conflict' && wantedSvc && wantedSvc !== cartSvc) {
        alts.filter(function (a) { return a.service === wantedSvc; }).forEach(function (a) { candidates.push(a); });
      } else {
        candidates = alts;
      }
      var items = normalizeItems(d.payload);
      var spec = { title: copy.title, text: copy.text, icon: copy.icon, actions: [] };
      var open = function () {
        spec.actions = leads.concat(switches);
        if (!cartSvc) spec.actions.push(keep); // no service yet: Cancel closes
        else if (!spec.actions.length) { keep.primary = true; spec.actions.push(keep); } // nothing else to offer: the dismiss leads
        openDialog(INTERCEPT_ID, spec, opener);
      };
      if (!candidates.length || !items.length) { open(); return; }
      // the consequence box: how many cart lines the switch removes ("{n} items
      // can’t be shipped. We’ll remove them from your cart."); a paused service
      // is never offered; the item must exist for it
      Promise.all(candidates.map(function (a) {
        if (a.paused) return null;
        return Promise.all(items.map(function (it) { return check(it.id, a.mode, it.quantity, d.opts || {}); })).then(function (checks) {
          if (checks.some(function (x) { return !x.ok; })) return null;
          return cartSvc ? checkCart(a.mode).then(function (res) { return res.cartReason ? null : { alt: a, res: res, n: distinct(res.unavailable).length }; }) : { alt: a, res: null, n: 0 };
        });
      })).then(function (rows) {
        rows.filter(Boolean).forEach(function (row) {
          var a = row.alt;
          var consequence = row.n ? plural('cant_' + a.service, row.n, {}) : '';
          switches.push({ label: cartSvc && !missing ? t('intercept_change_' + a.service) : t('switch_to_' + a.service), primary: !cartSvc, attrs: { 'data-intercept-switch': '', 'data-service': a.service }, onClick: function (b, m) {
            if (!cartSvc) { // no cart mode yet: the add itself writes the mode
              m.hide();
              d.retry(a.mode);
              return;
            }
            // the whole-order switch: the consequence box already named the
            // lines — they are removed and the mode written here, no second
            // dialog (design 3.1), then the add runs again in the new service
            if (busy) return;
            busy = true;
            setBusy(b, true);
            // the removals can empty the cart for a moment (preview 2026-10-23: a
            // ship cart of one DC-only line → "Pick up my whole order instead"
            // dropped the mode on the empty repaint and the add never landed):
            // the empty-cart clear stays off until the retried add resolves, as
            // the dry run's Continue does (core d.proceed)
            state.hold += 1;
            hold();
            var release = function () { state.hold = Math.max(0, state.hold - 1); };
            doSwitch(a.mode, row.res, 'intercept').then(function () {
              busy = false;
              m.hide();
              return d.retry(a.mode).then(release, release);
            }, function () {
              release();
              busy = false;
              setBusy(b, false);
              renderAside();
              sync();
              refreshPageCart(); // lines were removed before the write failed
            });
          } });
          if (consequence) spec.consequence = spec.consequence ? spec.consequence + ' · ' + consequence : consequence;
        });
        open();
      }, open);
    }

    /* ---- the "Removed from your cart" list (design 4.7): name + why + Find similar ---- */
    function asideRow(line) {
      var li = el('li', 'paex-cart__aside-row');
      li.setAttribute('data-aside-row', '');
      li.setAttribute('data-key', line.key);
      var thumb = el('span', 'paex-cart__aside-thumb');
      if (line.image) {
        var img = document.createElement('img');
        img.alt = '';
        img.loading = 'lazy';
        img.width = 48;
        img.height = 48;
        img.src = line.image;
        thumb.appendChild(img);
      }
      li.appendChild(thumb);
      var main = el('div', 'paex-cart__aside-main');
      var name = el('span', 'paex-cart__aside-name', lineName(line));
      if (line.title) name.title = line.title; // the full product title on hover / for assistive tech
      main.appendChild(name);
      // "Not available for shipping" / "Not in stock at {store}" (aside_reason_*,
      // lowercase in the table for an inline use; sentence case on its own line)
      var reason = el('p', 'paex-cart__aside-reason', capitalize(t('aside_reason_' + (line.asideFor || 'ship'), { store: line.storeName || '' })));
      reason.setAttribute('data-aside-reason', '');
      main.appendChild(reason);
      var acts = el('div', 'paex-cart__aside-actions');
      var similar = el('a', 'paex-cart__similar', t('find_similar'));
      similar.setAttribute('data-aside-similar', '');
      similar.href = line.similar || line.url || '#';
      acts.appendChild(similar);
      main.appendChild(acts);
      li.appendChild(main);
      return li;
    }
    // the session list (sessionStorage 'paex:aside'): the lines a switch
    // removed, each with Find similar only — no Add back, no switch-back (CHANGES 21)
    function renderAside() {
      var d = drawerEl();
      var box = d && d.querySelector('[data-fulfil-aside]');
      var list = box && box.querySelector('[data-aside-list]');
      if (!list) return;
      var rows = aside.list();
      clear(list);
      rows.forEach(function (line) { list.appendChild(asideRow(line)); });
      box.hidden = !rows.length;
      d.toggleAttribute('data-fulfil-aside-open', rows.length > 0);
    }

    /* ---- cart page banner (sections/paex-cart-fulfil.liquid) ---- */
    var bannerTimer = null;
    function bannerEl() { return document.querySelector('[data-paex-cart-fulfil]'); }
    function syncBanner() {
      var host = bannerEl();
      var head = host && host.querySelector('[data-fulfil-header]');
      if (host && !head) host.hidden = true;
      else if (host) host.hidden = !state.mode;
      var verdict = host && head && state.mode ? fillHeader(head, host) : null;
      syncPageCta(verdict);
      syncPageSummary();
    }
    // the cart PAGE's native summary (snippets/cart-details.liquid, frozen
    // markup) in a pickup / same-day cart (finding 27): the "Estimate shipping"
    // link and its "/" separator are hidden ("Add a note" stays) and the
    // "Taxes, Discounts and shipping calculated at checkout" note is replaced
    // by taxes_note_store; ship mode / no mode put both back (no reload). The
    // original note's nodes are kept aside (no innerHTML) and restored as they
    // were. Nothing is touched while nothing is managed — the page DOM stays
    // byte-for-byte what the server printed for a visitor with no mode.
    var taxKeep = typeof WeakMap === 'function' ? new WeakMap() : null;
    function syncPageSummary() {
      var main = document.getElementById('Main-Cart-Details');
      if (!main) return;
      var svc = state.mode ? service() : null;
      var storeMode = svc === 'pickup' || svc === 'local';
      var est = main.querySelector('.cart-shipping-estimate-button');
      var sep = est && est.previousElementSibling && est.previousElementSibling.classList.contains('separate') ? est.previousElementSibling : null;
      var tax = main.querySelector('.footer-tax');
      if (storeMode) {
        [est, sep].forEach(function (node) {
          if (node && !node.hasAttribute('data-fulfil-hidden')) { node.setAttribute('data-fulfil-hidden', ''); node.hidden = true; }
        });
        if (tax && taxKeep && !tax.hasAttribute('data-fulfil-tax')) {
          var keep = document.createDocumentFragment();
          while (tax.firstChild) keep.appendChild(tax.firstChild);
          taxKeep.set(tax, keep);
          tax.setAttribute('data-fulfil-tax', '');
          tax.appendChild(document.createTextNode(t('taxes_note_store')));
        }
        return;
      }
      [est, sep].forEach(function (node) {
        if (node && node.hasAttribute('data-fulfil-hidden')) { node.removeAttribute('data-fulfil-hidden'); node.hidden = false; }
      });
      if (tax && tax.hasAttribute('data-fulfil-tax')) {
        var kept = taxKeep && taxKeep.get(tax);
        clear(tax);
        if (kept) tax.appendChild(kept);
        tax.removeAttribute('data-fulfil-tax');
      }
    }
    // the cart PAGE's native checkout button (snippets/cart-details.liquid,
    // frozen markup): the drawer's CTA rule mirrored — the service suffix under
    // the label, disabled + the reason when the pre-flight fails. The base
    // reason (service paused / flagged lines) and the suffix come from the
    // banner's Liquid (data-cta-base / data-cta-suffix on [data-fulfil-header],
    // sections/paex-cart-fulfil.liquid); the same-day verdict rides on top.
    // No mode (every visitor today): the button, #Main-Cart-Details and its
    // observer are never touched — the page DOM stays byte-for-byte what the
    // server printed; a mode that goes away puts it all back (unbinds too).
    function syncPageCta(verdict) {
      var main = document.getElementById('Main-Cart-Details');
      var cta = main ? main.querySelector('.cart-checkout-button') : null;
      if (!cta) return;
      var host = bannerEl();
      var head = host && host.querySelector('[data-fulfil-header]');
      var suffixEl = cta.querySelector('[data-fulfil-cta-suffix]');
      var reasonEl = main.querySelector('[data-fulfil-cta-reason]');
      if (!state.mode || !head) {
        if (cta.hasAttribute('data-fulfil-managed')) {
          cta.removeAttribute('data-fulfil-managed');
          cta.classList.remove('paex-cart__pagecta');
          cta.disabled = false;
          cta.removeAttribute('aria-disabled');
          cta.removeAttribute('aria-describedby');
          cta.removeAttribute('data-fulfil-blocked');
          if (suffixEl) suffixEl.remove();
          if (reasonEl) reasonEl.remove();
        }
        unbindPageCta(main);
        return;
      }
      bindPageCta(main); // lazily, the first time a mode reaches the cart page
      cta.setAttribute('data-fulfil-managed', '');
      cta.classList.add('paex-cart__pagecta');
      var suffix = head.getAttribute('data-cta-suffix') || '';
      if (!suffixEl) {
        suffixEl = el('span', 'paex-cart__ctasuffix');
        suffixEl.setAttribute('data-fulfil-cta-suffix', '');
        cta.appendChild(suffixEl);
      }
      setText(suffixEl, suffix);
      suffixEl.hidden = !suffix;
      if (!reasonEl) {
        reasonEl = el('p', 'paex-cart__ctareason paex-cart__ctareason--page');
        reasonEl.id = 'Paex-Fulfil-Page-Cta-Reason';
        reasonEl.setAttribute('data-fulfil-cta-reason', '');
        reasonEl.setAttribute('role', 'status');
        // after the button row (.ctas-button-group is a flex row), else right after the button
        var group = cta.parentNode && cta.parentNode.classList && cta.parentNode.classList.contains('ctas-button-group') ? cta.parentNode : cta;
        group.insertAdjacentElement('afterend', reasonEl);
      }
      var base = head.getAttribute('data-cta-base') || '';
      var extra = verdict && (verdict.service === 'local' || verdict.service === 'pickup') && !verdict.ok ? (verdict.text || '') : '';
      var text = base || extra;
      cta.disabled = Boolean(text);
      cta.toggleAttribute('data-fulfil-blocked', Boolean(text));
      if (text) { cta.setAttribute('aria-disabled', 'true'); cta.setAttribute('aria-describedby', reasonEl.id); }
      else { cta.removeAttribute('aria-disabled'); cta.removeAttribute('aria-describedby'); }
      setText(reasonEl, text);
      reasonEl.hidden = !text;
    }
    // cart.js re-renders #Main-Cart-Details (innerHTML) on every change: the
    // suffix / reason printed above vanish with it, so the page button is
    // re-synced after each swap (debounced). Convergence: observer callbacks
    // arrive as microtasks after the synchronous write, so no "busy" flag can
    // mask our own writes — instead every record whose target is, or whose
    // added / removed nodes are, our suffix / reason element (its text-node
    // swaps included) is ignored by shape; attributes are not observed. A
    // sync therefore never schedules another sync, whatever setText does.
    var pageCtaTimer = null;
    var pageCtaObserver = null;
    function ownsCtaNode(node) { return Boolean(node && node.nodeType === 1 && (node.hasAttribute('data-fulfil-cta-suffix') || node.hasAttribute('data-fulfil-cta-reason') || node.hasAttribute('data-fulfil-tax'))); }
    function bindPageCta(main) {
      if (!main || main.dataset.paexFulfilCta || typeof MutationObserver !== 'function') return;
      main.dataset.paexFulfilCta = '1';
      if (pageCtaObserver) pageCtaObserver.disconnect(); // the element itself was replaced
      pageCtaObserver = new MutationObserver(function (records) {
        if (!state.mode) return;
        var foreign = records.some(function (r) {
          if (ownsCtaNode(r.target)) return false; // text written inside our suffix / reason
          return Array.prototype.some.call(r.addedNodes, function (x) { return !ownsCtaNode(x); }) || Array.prototype.some.call(r.removedNodes, function (x) { return !ownsCtaNode(x); });
        });
        if (!foreign) return; // our own suffix / reason nodes
        clearTimeout(pageCtaTimer);
        pageCtaTimer = setTimeout(function () { syncBanner(); }, 50);
      });
      pageCtaObserver.observe(main, { childList: true, subtree: true });
    }
    function unbindPageCta(main) {
      if (pageCtaObserver) { pageCtaObserver.disconnect(); pageCtaObserver = null; }
      clearTimeout(pageCtaTimer);
      if (main && main.dataset.paexFulfilCta) delete main.dataset.paexFulfilCta;
    }
    // the section is re-read (Section Rendering API) after every cart change
    // and mode change, like paex-cart-deadline.js
    function reloadBanner() {
      var host = bannerEl();
      if (!host) return;
      var section = host.getAttribute('data-section');
      if (!section) return;
      clearTimeout(bannerTimer);
      bannerTimer = setTimeout(function () {
        var url = (host.getAttribute('data-url') || root() + 'cart') + '?section_id=' + encodeURIComponent(section);
        fetch(url, { cache: 'no-store', credentials: 'same-origin' })
          .then(function (response) { return response.ok ? response.text() : null; })
          .then(function (html) {
            if (html == null || !host.isConnected) return;
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.querySelector('[data-paex-cart-fulfil]');
            if (!fresh) { host.hidden = true; return; }
            var inner = host.querySelector('[data-fulfil-banner-inner]');
            var freshInner = fresh.querySelector('[data-fulfil-banner-inner]');
            if (inner && freshInner) inner.replaceWith(freshInner);
            syncBanner();
          })
          .catch(function () { syncBanner(); });
      }, 150);
    }
    function onBannerClick(event) {
      var host = bannerEl();
      if (!host || !host.contains(event.target)) return;
      var change = event.target.closest('[data-fulfil-change]');
      if (change) { event.preventDefault(); openChooser(change); }
    }

    /* ---- modal shells: their inner clicks never reach document ---- */
    function bindShells() {
      [CHOOSER_ID, INTERCEPT_ID].forEach(function (id) {
        var m = modal(id);
        if (!m || m.dataset.paexFulfilBound) return;
        m.dataset.paexFulfilBound = '1';
        // keep the drawer's own Escape handling out of our dialogs
        m.addEventListener('keydown', function (event) { event.stopPropagation(); });
      });
    }
    function bindDrawerOpen() {
      var d = drawerEl();
      if (!d || d.dataset.paexFulfilUi) return;
      d.dataset.paexFulfilUi = '1';
      // open → paex-cart.js bindOpenFreshness calls ui.sync() (same hook as
      // the clock / stock freshness); close → the after-add strip goes
      var wasOpen = isOpen(d);
      new MutationObserver(function () {
        var open = isOpen(d);
        if (!open && wasOpen) hideStrip();
        wasOpen = open;
      }).observe(d, { attributes: true, attributeFilter: ['open'] });
    }
    function boot() {
      document.addEventListener(EVT_INTERCEPT, onIntercept);
      document.addEventListener(EVT_CHANGE, function () {
        // a service change retires the after-add strip of the previous service (it names that service)
        var d = drawerEl();
        var strip = d && d.querySelector('[data-fulfil-strip]');
        if (strip && !strip.hidden && strip.getAttribute('data-service') !== (service() || '')) hideStrip();
        sync();
        reloadBanner();
      });
      // the EDD strip painted a (live) ship date: the ship header / strip follow it
      document.addEventListener('paex:cart-edd', function () { if (service() === 'ship') { sync(); var d2 = drawerEl(); var s2 = d2 && d2.querySelector('[data-fulfil-strip]'); if (s2 && !s2.hidden) showStrip(); } });
      document.addEventListener(EVT_ORIGIN, function () { sync(); });
      document.addEventListener('paex:dest-change', function (event) { mirrorPostal(event && event.detail); sync(); });
      document.addEventListener('click', onBannerClick);
      bindShells();
      bindDrawerOpen();
      // the cart page observer binds lazily from syncPageCta (only once a mode exists)
      document.addEventListener('shopify:section:load', function () { bindShells(); bindDrawerOpen(); sync(); });
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { bindShells(); bindDrawerOpen(); sync(); }, { once: true });
      else sync();
    }

    return {
      sync: sync, syncDrawer: syncDrawer, syncBanner: syncBanner, reloadBanner: reloadBanner, syncPageSummary: syncPageSummary,
      postalSheet: postalSheet, sheetAvailable: sheetAvailable,
      openChooser: openChooser, askSwitch: askSwitch, renderAside: renderAside, syncPageCta: syncPageCta,
      postalApply: postalApply, showStrip: showStrip, hideStrip: hideStrip, onCartUpdate: onCartUpdate,
      alternatives: alternatives, fillHeader: fillHeader, shipDate: shipDate, boot: boot,
    };
  })();

  /* ------------------------------------------------------------------ *
   * Boot + subscriptions
   * ------------------------------------------------------------------ */
  function bindPubSub(attempt) {
    if (typeof webvista === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined' || typeof webvista.subscribe !== 'function') {
      if ((attempt || 0) < 50) setTimeout(function () { bindPubSub((attempt || 0) + 1); }, 100);
      return;
    }
    webvista.subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
      var data = payload && payload.cartData;
      // the add that followed a gate write has landed (an ADD answers with the
      // line item(s), no item_count) → the empty-cart clear may run again
      if (data && typeof data === 'object' && !data.status && data.item_count === undefined && (typeof data.key === 'string' || Array.isArray(data.items))) unhold();
      rememberCart(data); // a full cart → cache; a line item → invalidate
      ui.onCartUpdate(payload); // lane D: the after-add strip / the cart page banner
    });
  }
  // the pilot was switched off at the cart's store (1.10): the attribute is
  // cleared on the spot — the drawer and the cart page then render as live
  function clearRetired() {
    if (!state.mode || !modeRetired()) return false;
    set(null, { source: 'retired' }).catch(function () {});
    return true;
  }
  function boot() {
    var carrier = readCarrier();
    if (carrier) {
      state.mode = carrier.count === 0 ? null : carrier.mode;
    }
    stamp();
    state.originReady = resolveOrigin();
    if (state.mode) {
      if (!clearRetired()) dispatch(EVT_CHANGE, changeDetail(null, 'boot', null));
    } else if (!carrier) {
      // no drawer on the page: learn the mode from the cart itself (cart page / page cart)
      cart(true).then(function (data) {
        if (!data || state.mode) return;
        var m = parseMode(data.attributes && data.attributes.paex_fulfil);
        if (m && data.item_count > 0) { state.mode = m; stamp(); if (!clearRetired()) dispatch(EVT_CHANGE, changeDetail(null, 'boot', data)); }
      });
    }
    document.addEventListener('paex:dest-change', function () { resolveOrigin(); });
    document.addEventListener('paex:geo-ready', function () { resolveOrigin(); });
    bindPubSub(0);
    ui.boot(); // lane D: strip, header fill, aside, chooser, interception, banner
  }

  window.paexFulfil = {
    // mode
    mode: mode, service: service, locationId: locationId, parseMode: parseMode, makeMode: makeMode,
    set: set, select: select, selection: selection, stamp: stamp, syncCarrier: syncCarrier,
    // store
    stores: stores, store: store, storeName: storeName, storeId: storeId, explicitStoreId: explicitStoreId,
    participates: participates, gated: gated, pausedToday: pausedToday, pauseReason: pauseReason, pausedBack: pausedBack,
    pauseText: pauseText, pauseFragment: pauseFragment, blackoutRange: blackoutRange, blackoutList: blackoutList, footprintRadius: footprintRadius, inZone: inZone, postalDistanceKm: postalDistanceKm,
    storeId: storeId, // the resolved store (chip / account / postal / geo) — the cards' store check (rcc-card-components.js)
    postalSheet: function (trigger) { return ui && typeof ui.postalSheet === 'function' ? ui.postalSheet(trigger) : false; },
    postalSheetAvailable: function () { return ui && typeof ui.sheetAvailable === 'function' ? ui.sheetAvailable() : false; },
    origin: origin, resolveOrigin: resolveOrigin, knownPostal: knownPostal, onlineLocId: onlineLocId,
    // stock + checks
    stock: stock, variantStock: variantStock, cart: cart, check: check, checkCart: checkCart, defaultMode: defaultMode, gate: gate, switchCart: switchCart,
    // wording
    readyLine: readyLine, localLine: localLine, fmtTime: fmtTime, fmtMinutes: fmtMinutes, t: t, texts: TEXTS, shared: SHARED, days: DAYS,
    reasonText: reasonText, interceptText: interceptText, interceptCopy: interceptCopy, dryrunText: dryrunText, functionMessage: functionMessage, money: money, moneyCents: moneyCents,
    shortName: shortName, lineName: lineName, names: names, fmtDateShort: fmtDateShort,
    // misc
    aside: aside, tz: tz, lang: LANG, settings: SETTINGS,
    events: { change: EVT_CHANGE, select: EVT_SELECT, intercept: EVT_INTERCEPT, origin: EVT_ORIGIN },
    // UI layer (lane D): header fill, strip, aside, chooser, interception, banner
    ui: ui,
  };

  boot();
})();
